import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import VoiceDictationInput from '../components/VoiceDictationInput';
import SignatureInput from '../components/SignatureInput';
import { FileText, Save, ArrowLeft, Printer, ChevronLeft, ChevronRight } from 'lucide-react';
import { exportFillableFromDom } from '../utils/fillable';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RecertificationAssessmentForm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const formId = searchParams.get('id');
  const appointmentId = searchParams.get('appointmentId');
  const [visitInfo, setVisitInfo] = useState(null);
  
  const [currentSection, setCurrentSection] = useState(0);
  const [saving, setSaving] = useState(false);
  const [patients, setPatients] = useState([]);
  
  // Comprehensive form state matching the Recertification PDF
  const [formData, setFormData] = useState({
    // Patient Info
    patientId: '',
    patientName: '',
    idNumber: '',
    
    // Section A - Administrative Information
    m0080Discipline: '', // 1=RN, 2=PT, 3=SLP/ST, 4=OT
    visitType: '', // Skilled, Skilled & Supervisory, Other
    timeIn: '',
    timeOut: '',
    visitDate: '',
    m0090DateAssessmentCompleted: '',
    m0100ReasonForAssessment: '', // 1=Follow-Up, 4=Recertification, 5=Other
    m0100ExplainOther: '',
    m0110EpisodeTiming: '', // 1=Early, 2=Later, UK=Unknown, NA=Not Applicable
    
    // Certification Period (auto-calculated)
    certificationPeriodFrom: '',
    certificationPeriodTo: '', // Auto-calculated: +60 days from start
    nextRecertDueDate: '', // Auto-calculated: 5 days before cert end
    
    // Patient Contacts/Caregivers
    contactConfirmedWithPatient: false,
    contactConfirmedWithCaregiver: false,
    presentFamilyMembers: false,
    presentRepresentative: false,
    presentCaregivers: false,
    presentOther: false,
    presentOtherSpecify: '',
    hasRepresentative: '',
    representativeType: '', // Court declared, Patient selected
    representativeName: '',
    representativeRelationship: '',
    representativeRelationshipOther: '',
    representativeAddress: '',
    representativeCity: '',
    representativeState: '',
    representativeZip: '',
    representativePhone: '',
    representativeEmail: '',
    
    // Primary Caregiver
    primaryCaregiverNA: false,
    primaryCaregiverNoneAvailable: false,
    primaryCaregiverName: '',
    primaryCaregiverRelationship: '',
    primaryCaregiverAddress: '',
    primaryCaregiverCity: '',
    primaryCaregiverState: '',
    primaryCaregiverZip: '',
    primaryCaregiverPhone: '',
    primaryCaregiverEmail: '',
    
    // Paid Services
    paidServiceOtherThanHomeHealth: '',
    paidServiceCompanyName: '',
    paidServicePhone: '',
    paidServiceContactName: '',
    noChangeSinceLastAssessmentContacts: false,
    
    // Emergency Preparedness
    emergencyContactType: '', // Representative, Caregiver, Other
    emergencyContactName: '',
    emergencyContactRelationship: '',
    emergencyContactAddress: '',
    emergencyContactCity: '',
    emergencyContactState: '',
    emergencyContactZip: '',
    emergencyContactPhone: '',
    emergencyContactEmail: '',
    
    // Critical Situation Contact
    criticalContactAvailable: '',
    criticalContactName: '',
    criticalContactPhone: '',
    
    // Supportive Assistance Summary
    noChangeSinceLastAssessmentSupport: false,
    caregiverAssistsWithADLs: '',
    assistanceTypeMeals: false,
    assistanceTypeADLs: false,
    assistanceTypeTransportation: false,
    assistanceTypeSupervision: false,
    assistanceTypeMedications: false,
    assistanceTypeHomeMaintenance: false,
    assistanceTypeOther: false,
    assistanceTypeOtherSpecify: '',
    caregiverWillingToAssist: '',
    caregiverWillingExplain: '',
    caregiverNeedsTraining: '',
    caregiverTrainingExplain: '',
    caregiverNoSetSchedule: false,
    caregiverScheduleSundayAM: '',
    caregiverScheduleSundayPM: '',
    caregiverScheduleSundayNight: '',
    caregiverScheduleMondayAM: '',
    caregiverScheduleMondayPM: '',
    caregiverScheduleMondayNight: '',
    caregiverScheduleTuesdayAM: '',
    caregiverScheduleTuesdayPM: '',
    caregiverScheduleTuesdayNight: '',
    caregiverScheduleWednesdayAM: '',
    caregiverScheduleWednesdayPM: '',
    caregiverScheduleWednesdayNight: '',
    caregiverScheduleThursdayAM: '',
    caregiverScheduleThursdayPM: '',
    caregiverScheduleThursdayNight: '',
    caregiverScheduleFridayAM: '',
    caregiverScheduleFridayPM: '',
    caregiverScheduleFridayNight: '',
    caregiverScheduleSaturdayAM: '',
    caregiverScheduleSaturdayPM: '',
    caregiverScheduleSaturdayNight: '',
    
    // Advance Directives
    noChangeSinceLastAssessmentDirectives: false,
    hasAdvanceDirectives: '',
    advanceDirectivesObtained: false,
    advanceDirectivesChanged: false,
    doCPR: false,
    doNotIntubate: false,
    livingWill: false,
    doNotResuscitate: false,
    noArtificialNutrition: false,
    medicalPowerOfAttorneyName: '',
    medicalPowerOfAttorneyPhone: '',
    financialPowerOfAttorneyName: '',
    financialPowerOfAttorneyPhone: '',
    stateSpecificForms: '',
    copiesOnFileWithPCP: false,
    copiesOnFileWithOther: false,
    copiesOnFileOtherSpecify: '',
    advanceDirectivesComments: '',
    
    // Sensory Status
    patientWearsGlasses: false,
    patientWearsContactsR: false,
    patientWearsContactsL: false,
    patientWearsProsthesisR: false,
    patientWearsProsthesisL: false,
    patientWearsHearingAidR: false,
    patientWearsHearingAidL: false,
    patientWearsOther: '',
    sensoryAffectedEyes: false,
    sensoryAffectedEars: false,
    sensoryAffectedNose: false,
    sensoryAffectedMouth: false,
    sensoryAffectedThroat: false,
    sensoryAffectedSight: false,
    sensoryAffectedHearing: false,
    sensoryAffectedSmell: false,
    sensoryAffectedTaste: false,
    sensoryStructuralImpairment: '',
    sensoryFunctionalImpairment: '',
    sensoryActivityLimitation: '',
    sensoryNursingAddressesImpairment: '',
    
    // Neurological Status
    neurologicalNoProblem: false,
    diagnosedNeuroDisorder: '',
    historyTraumaticBrainInjury: false,
    traumaticBrainInjuryDate: '',
    historyHeadaches: false,
    lastHeadacheDate: '',
    historySeizures: false,
    lastSeizureDate: '',
    aphasicReceptive: false,
    aphasicExpressive: false,
    tremorsAtRest: false,
    tremorsWithMovement: false,
    tremorsContinuous: false,
    spasmsLocation: '',
    dominantSide: '', // Right, Left
    hemiplegiaRight: false,
    hemiplegiaLeft: false,
    paraplegia: false,
    quadriplegia: false,
    neuroAffectsFunctionalAbility: '',
    neuroAffectsExplain: '',
    
    // Cognitive Status
    cognitiveFunction: '', // Alert/oriented, Requires prompting, etc.
    patientConfused: '', // Constantly, Non-responsive, Never, etc.
    patientAnxious: '', // None, Less often than daily, Daily, All the time
    memoryDeficit: false,
    impairedDecisionMaking: false,
    disruptiveBehaviorsVerbal: false,
    disruptiveBehaviorsPhysical: false,
    paranoidBehaviors: false,
    delusional: false,
    noBehaviorsDemonstrated: false,
    receivingPsychiatricServices: '',
    
    // Mental Status
    mentalStatusNA: false,
    mentalStatusDescription: '',
    suddenMentalStatusChange: '',
    changeCoincidedWithSomething: '',
    changeCoincidedExplain: '',
    mentalStatusChangeReportedBy: '', // Patient, Caregiver, Representative, Other
    
    // Psychosocial
    canCommunicateNeeds: '',
    cannotCommunicateExplain: '',
    primaryCommunicationMethod: '',
    communicationBarrierIntervention: '',
    supportPersonPresentName: '',
    supportPersonRelationship: '',
    spiritualResource: '',
    spiritualResourcePhone: '',
    noChangeSinceLastVisitPsychosocial: false,
    feelingsAngry: false,
    feelingsFear: false,
    feelingsSadness: false,
    feelingsDiscouraged: false,
    feelingsLonely: false,
    feelingsDepressed: false,
    feelingsHelpless: false,
    feelingsContent: false,
    feelingsHappy: false,
    feelingsHopeful: false,
    feelingsMotivated: false,
    feelingsOther: '',
    feelingsNA: false,
    sleepAdequate: false,
    sleepInadequate: false,
    restAdequate: false,
    restInadequate: false,
    napFrequency: '',
    hoursSleptPerNight: '',
    sleepExplain: '',
    inappropriateBehaviorsToward: '', // Caregiver, Clinician, Representative, Others
    inappropriateBehaviorsReported: false,
    inappropriateBehaviorsObserved: false,
    inappropriateBehaviorsNA: false,
    inappropriateBehaviorsDescribe: '',
    inabilityToCope: false,
    unrealisticExpectations: false,
    denialOfProblems: false,
    lackOfMotivation: false,
    inabilityToRecognize: false,
    evidenceAbuseNeglect: '', // Abuse, Neglect, Exploitation, Verbal, Emotional, Physical, Financial
    evidencePotential: false,
    evidenceActual: false,
    evidenceNA: false,
    mswReferralMade: '',
    otherIntervention: '',
    psychosocialAffectsFunctionalAbility: '',
    psychosocialAffectsExplain: '',
    
    // Care Preferences/Goals
    communicatedCarePreferences: '',
    carePreferencesList: '',
    communicatedPersonalGoals: '',
    noPersonalGoalsReason: '', // Don't want, Already have, Other
    goalsRealisticAgreed: false,
    goalsModifiedAgreed: false,
    actionsIdentifiedAgreed: false,
    helpedWriteGoals: false,
    goalsAddedToCarePlan: false,
    progressTowardsGoals: '',
    
    // Strengths/Limitations
    patientStrengths: '',
    structuralImpairment: '',
    functionalImpairment: '',
    impairmentLimitsActivities: '',
    impairmentLimitsExplain: '',
    nursingAddressesImpairments: '',
    nursingAddressesExplain: '',
    significantChangesInStrengths: '',
    significantChangesExplain: '',
    
    // Safety Measures
    safetyBleedingPrecautions: false,
    safetySiderailsUp: false,
    safetyInfectionControl: false,
    safetyO2Precautions: false,
    safetyElevateHeadOfBed: false,
    safetyWalkerCane: false,
    safetySeizurePrecautions: false,
    safety24HrSupervision: false,
    safetyOther: '',
    safetyFallPrecautions: false,
    safetyClearPathways: false,
    safetyAspirationPrecautions: false,
    safetyLockWCWithTransfers: false,
    emergencyPrepChanges: '',
    emergencyPrepChangesExplain: '',
    
    // Section G - Functional Status
    m1800Grooming: '', // 0-3
    m1810DressUpperBody: '', // 0-3
    m1820DressLowerBody: '', // 0-3
    m1830Bathing: '', // 0-6
    m1840ToiletTransferring: '', // 0-4
    m1850Transferring: '', // 0-5
    m1860Ambulation: '', // 0-6
    
    indicationsForHomeHealthAides: '',
    homeHealthAidesRefused: false,
    homeHealthAidesOrderObtained: '',
    homeHealthAidesReasonForNeed: '',
    indicationsForOT: '',
    otRefused: false,
    otOrderObtained: '',
    otReasonForNeed: '',
    
    // Activities Permitted
    activitiesNoRestrictions: false,
    activitiesCompleteBedrest: false,
    activitiesBathroomPrivileges: false,
    activitiesUpAsTolerated: false,
    activitiesTransferBedChair: false,
    activitiesExercisesPrescribed: false,
    activitiesPartialWeightBearing: false,
    activitiesIndependentInHome: false,
    activitiesCrutches: false,
    activitiesCane: false,
    activitiesWheelchair: false,
    activitiesWalker: false,
    activitiesOther1: '',
    activitiesOther2: '',
    activitiesOther3: '',
    adlPlanComments: '',
    
    // Functional Limitations
    limitationAmputation: false,
    limitationParalysis: false,
    limitationLegallyBlind: false,
    limitationBowelBladder: false,
    limitationEndurance: false,
    limitationDyspnea: false,
    limitationContracture: false,
    limitationAmbulation: false,
    limitationOther1: '',
    limitationHearing: false,
    limitationSpeech: false,
    limitationOther2: '',
    
    // Section GG - Functional Abilities and Goals
    gg0130AEating: '',
    gg0130BOralHygiene: '',
    gg0130CToiletingHygiene: '',
    gg0130E1ShowerBatheSelf: '',
    gg0130E2ShowerBatheSelfWheelchair: '',
    gg0130FUpperBodyDressing: '',
    gg0130GLowerBodyDressing: '',
    gg0130HPuttingOnTakingOffFootwear: '',
    
    gg0170AAmbience: '',
    gg0170BTransfer: '',
    gg0170CWalkingRoom: '',
    gg0170DWalkingStairs: '',
    
    // Pain Assessment
    painFrequency: '', // 0=Never, 1=Less than daily, 2=Daily, 3=More than 3 times/day
    painPrimarySite: '',
    painIntensity: '', // 0-10
    painRadiates: '', // No, Occasionally, Continuously, Intermittent
    painControlMedicationsAdequate: '',
    painMedAnalgesics: false,
    painMedCorticosteroid: false,
    painMedAntianxiety: false,
    painMedDMARD: false,
    painMedAnticonvulsant: false,
    painMedLocalAnesthetics: false,
    painMedAntidepressant: false,
    painMedNarcotic: false,
    painMedAntimigraine: false,
    painMedNSAIDs: false,
    painMedBiologic: false,
    painMedSalicylate: false,
    painComments: '',
    
    // Cardiopulmonary
    cardiopulmonaryNoProblem: false,
    diagnosedCardioDisorder: '',
    breathSoundsAnteriorRight: '',
    breathSoundsAnteriorLeft: '',
    breathSoundsPosteriorRightUpper: '',
    breathSoundsPosteriorLeftUpper: '',
    breathSoundsPosteriorRightLower: '',
    breathSoundsPosteriorLeftLower: '',
    laboredBreathing: false,
    nonSmoker: false,
    hasEverSmoked: '',
    lastSmokedDate: '',
    smokerFrequency: '', // Daily, Occasional, Very Occasional
    smokerDailyAmount: '',
    oxygenIntermittent: false,
    oxygenContinuous: false,
    ventilatorContinuous: false,
    ventilatorAtNight: false,
    positiveAirwayPressureContinuous: false,
    positiveAirwayPressureBilevel: false,
    o2LPM: '',
    o2ViaCannula: false,
    o2ViaMask: false,
    o2ViaTrach: false,
    o2Saturation: '',
    trachSizeType: '',
    trachManagedBy: '', // Patient, RN, Caregiver, Family
    intermittentTreatments: '',
    intermittentTreatmentsExplain: '',
    coughNo: false,
    coughYesProductive: false,
    coughYesNonProductive: false,
    coughDescribe: '',
    positioningNecessary: '',
    positioningDescribe: '',
    heartSoundsRegular: false,
    heartSoundsIrregular: false,
    pacemakerDate: '',
    pacemakerLastChecked: '',
    colorOfNailBeds: '',
    edemaPedalRightValue: '',
    edemaPedalRightCapillaryRefill: '',
    edemaPedalLeftValue: '',
    edemaPedalLeftCapillaryRefill: '',
    extremityCrampsLocation: '',
    painAtRestValue: '',
    painAtRestCapillaryRefill: '',
    dependentValue: '',
    dependentCapillaryRefill: '',
    isSob: '',
    sobAssessed: false,
    sobReported: false,
    sobExplain: '',
    respiratoryAffectsFunctionalAbility: '',
    respiratoryAffectsExplain: '',
    diseaseManagementProblems: '',
    
    // Vital Signs
    temperature: '',
    temperatureMethod: '', // Oral, Temporal, Rectal, Axillary, Tympanic
    bloodPressureAtRest: '',
    bloodPressureSittingLying: '',
    bloodPressureStanding: '',
    pulseMethod: '', // Apical, Brachial, Radial, Carotid
    pulseRegular: false,
    pulseIrregular: false,
    pulseWithActivity: false,
    pulsePostActivity: false,
    pulseRate: '',
    pulseOximetryAtRest: '',
    pulseOximetryAfterActivity: '',
    pulseOximetryActivityType: '',
    respirationsRegular: false,
    respirationsIrregular: false,
    apneaPeriods: '',
    apneaObserved: false,
    apneaReported: false,
    respirationRate: '',
    
    // Height and Weight
    heightActual: false,
    heightReported: false,
    heightValue: '',
    weightActual: false,
    weightNotWeighed: false,
    weightNotWeighedReason: '',
    weightValue: '',
    weightChangeNA: false,
    weightChangeGain: false,
    weightChangeLoss: false,
    weightChangeAmount: '',
    weightChangePeriod: '', // week, month, year
    
    // Nutritional Status
    nutritionalNoProblem: false,
    dietGeneral: false,
    dietNAS: false,
    dietNPO: false,
    dietControlledCarb: false,
    dietRenal: false,
    dietOther: '',
    nutritionalRequirements: '',
    appetiteGood: false,
    appetiteFair: false,
    appetitePoor: false,
    increaseFluidAmount: '',
    restrictFluidAmount: '',
    nausea: false,
    vomitingFrequency: '',
    heartburn: false,
    foodIntolerance: '',
    foodEnvironmentalAllergies: '',
    knownAllergiesNA: false,
    alcoholUse: '',
    alcoholFrequency: '',
    alcoholDailyAmount: '',
    nutritionalApproachesParenteral: false,
    nutritionalApproachesFeedingTube: false,
    nutritionalApproachesMechanicallyAltered: false,
    nutritionalApproachesNA: false,
    nutritionalRiskScore: '',
    nutritionalAtRiskIntervention: '',
    nutritionalSafetyRisk: '',
    currentAbilityToPrepareMeals: '', // 0-3
    
    // Section M - Skin Conditions
    m1306UnhealedPressureUlcer: '', // 0=No, 1=Yes
    
    // Wound/Lesion Assessment (up to 5 wounds)
    wound1DateReported: '',
    wound1Location: '',
    wound1Type: '',
    wound1SizeL: '',
    wound1SizeW: '',
    wound1SizeD: '',
    wound1Tunneling: '',
    wound1Undermining: '',
    wound1Stage: '',
    wound1Odor: '',
    wound1SurroundingSkin: '',
    wound1Edema: '',
    wound1AppearanceSlough: '',
    wound1AppearanceEschar: '',
    wound1AppearanceGranulation: '',
    wound1DrainageAmount: '',
    wound1DrainageColor: '',
    wound1DrainageConsistency: '',
    wound1IncisionStatus: '',
    wound1DateHealed: '',
    wound1Comments: '',
    
    // Wound Care Section
    woundCareNA: false,
    woundCareDoneThisVisit: '',
    woundLocationSite: '',
    soiledDressingRemovedBy: '',
    techniqueUsed: '', // Sterile, Clean
    handsWashedBefore: false,
    handsWashedAfter: false,
    woundCleanedWith: '',
    woundDressingApplied: '',
    woundIrrigatedWith: '',
    dressingSecuredWith: '',
    woundPackedWith: '',
    soiledDressingDisposed: false,
    patientToleratedWell: '',
    woundCareComments: '',
    
    // Diabetic Foot Exam
    diabeticFootExamNA: false,
    diabeticFootExamFrequency: '',
    diabeticFootExamDoneBy: '',
    diabeticFootExamClinicianThisVisit: '',
    diabeticFootIntegumentFindings: '',
    pedalPulsesPresent: '',
    pedalPulsesAbsent: '',
    pedalPulsesComment: '',
    lossOfSenseWarm: '',
    lossOfSenseCold: '',
    sensationComment: '',
    numbnessRight: false,
    numbnessLeft: false,
    tinglingRight: false,
    tinglingLeft: false,
    burningRight: false,
    burningLeft: false,
    legHairPresent: '',
    legHairAbsent: '',
    diabeticFootComments: '',
    integumentaryAffectsFunctionalAbility: '',
    integumentaryAffectsExplain: '',
    integumentaryAtRisk: '',
    integumentaryAtRiskExplain: '',
    
    // Medications
    drugRegimenReviewDate: '',
    drugRegimenNoChange: false,
    drugRegimenOrderObtained: false,
    potentialAdverseEffects: false,
    drugReactions: false,
    ineffectiveDrugTherapy: false,
    significantSideEffects: false,
    significantDrugInteractions: false,
    duplicateDrugTherapy: false,
    nonComplianceWithDrugTherapy: false,
    highRiskDrugs: false,
    medicationComments: '',
    medicationAllergiesNone: false,
    medicationAllergiesAspirin: false,
    medicationAllergiesPenicillin: false,
    medicationAllergiesSulfa: false,
    medicationAllergiesOther: '',
    psychotropicDrugUse: '',
    financialAbilityForMedications: '',
    financialAbilityComment: '',
    mswReferralMadeForMedications: '',
    mswReferralComment: '',
    specialNeedsForMedications: '',
    specialNeedsExplain: '',
    
    // Infusion
    infusionNA: false,
    hasIV: '',
    ivTypes: '',
    ivNumberOfSites: '',
    ivTotalLumens: '',
    ivInsertionDates: '',
    ivSiteLocations: '',
    lumenPatent: '',
    lumenPatentExplain: '',
    flushSolutionFrequency: '',
    flushNA: false,
    injectionCapChangeFrequency: '',
    dressingChangeDuringVisit: '',
    dressingChangeFrequency: '',
    dressingChangeSterile: false,
    dressingChangeClean: false,
    dressingChangePerformedBy: '',
    ivSiteSkinCondition: '',
    infusionSolution: '',
    pumpType: '',
    infusionAdministeredBy: '',
    purposeOfIV: '',
    infusionProvidedThisVisit: '',
    infusionProvidedSpecify: '',
    
    // Immunizations
    influenzaLast12Months: '',
    pneumoniaVaccine: false,
    tetanusVaccine: false,
    shinglesVaccine: false,
    hepatitisCVaccine: false,
    otherVaccine: '',
    vaccinesNeeded: '',
    lastCovidVaccination: '', // Initial, Booster 1-5
    medicalRestrictionsImpactingImmunizations: '',
    
    // Refused Cares
    refusedCaresSinceLastAssessment: '',
    refusedCaresExplain: '',
    refusedCaresSignificant: '',
    refusedCaresSignificantExplain: '',
    
    // Patient/Caregiver Education
    woundCareKnowledgeDeficit: '',
    woundCareInstructed: '',
    diabeticFootCareKnowledgeDeficit: '',
    diabeticFootCareInstructed: '',
    insulinAdminKnowledgeDeficit: '',
    insulinAdminInstructed: '',
    glucometerKnowledgeDeficit: '',
    glucometerInstructed: '',
    nutritionalKnowledgeDeficit: '',
    nutritionalInstructed: '',
    medicationAdminKnowledgeDeficit: '',
    medicationAdminInstructed: '',
    medicationRouteOral: false,
    medicationRouteInjected: false,
    medicationRouteInfused: false,
    medicationRouteInhaled: false,
    medicationRouteTopical: false,
    painManagementKnowledgeDeficit: '',
    painManagementInstructed: '',
    oxygenUseKnowledgeDeficit: '',
    oxygenUseInstructed: '',
    medicalDevicesKnowledgeDeficit: '',
    medicalDevicesInstructed: '',
    pressureReductionKnowledgeDeficit: '',
    pressureReductionInstructed: '',
    catheterCareKnowledgeDeficit: '',
    catheterCareInstructed: '',
    trachCareKnowledgeDeficit: '',
    trachCareInstructed: '',
    ostomyCareKnowledgeDeficit: '',
    ostomyCareInstructed: '',
    emergencyPrepKnowledgeDeficit: '',
    emergencyPrepInstructed: '',
    infectionControlKnowledgeDeficit: '',
    infectionControlInstructed: '',
    ssReportToAgencyKnowledgeDeficit: '',
    ssReportToAgencyInstructed: '',
    patientsRightsKnowledgeDeficit: '',
    patientsRightsInstructed: '',
    otherCareEducation: '',
    teachBackMethodUsed: '',
    educatedThisVisitFor: '',
    educationWillContinue: false,
    hasActionPlanForExacerbation: '',
    
    // Recertification Summary
    homeboundYes: false,
    homeboundCriteria1DependentOnDevice: false,
    homeboundDeviceCrutches: false,
    homeboundDeviceCanes: false,
    homeboundDeviceWalker: false,
    homeboundDeviceWheelchairManual: false,
    homeboundDeviceWheelchairMotorized: false,
    homeboundDeviceProsthetic: false,
    homeboundDeviceScooter: false,
    homeboundDeviceHelper: false,
    homeboundDeviceOther: '',
    homeboundNeedsSpecialTransportation: '',
    homeboundNeedsPhysicalAssist: '',
    homeboundMedicallyContraindicated: '',
    homeboundCriteria2InfrequentOutings: '',
    homeboundTaxingEffort: '',
    improvementsNoted: '',
    difficultiesNoGains: '',
    continuedNursingCareNeeded: '',
    
    // Summary Checklist
    carePlanReviewed: false,
    carePlanRevised: false,
    carePlanInvolvementPatient: false,
    carePlanInvolvementRepresentative: false,
    carePlanInvolvementCaregiver: false,
    carePlanOutcomeAchieved: false,
    medicationRegimenCompleted: false,
    medicationStatusComments: '',
    careCoordinationPhysician: false,
    careCoordinationSN: false,
    careCoordinationPT: false,
    careCoordinationOT: false,
    careCoordinationSLP: false,
    careCoordinationMSW: false,
    careCoordinationAide: false,
    careCoordinationOther: '',
    mswReferralCommunityResources: false,
    mswReferralLivingWill: false,
    mswReferralCounseling: false,
    mswReferralUnsafeEnvironment: false,
    mswReferralOther: '',
    mswReferralDate: '',
    careCoordinationComments: '',
    referralTo: '',
    reasonForReferral: '',
    approximateNextVisitDate: '',
    planForNextVisit: '',
    recertification: '', // No=complete Discharge, Yes=complete remaining sections
    medicalNecessityDocumentation: '',
    verbalOrderObtained: '',
    verbalOrderDate: '',
    
    // Rehabilitation Potential
    rehabPotentialReturnToIndependent: false,
    rehabPotentialRemainWithPrimaryCaregiver: false,
    rehabPotentialRemainWithCommunityAgencies: false,
    rehabPotentialRestorative: false,
    dischargeDiscussedWith: '',
    changesSinceLastAssessment: '',
    anticipatedDischargeStatus: '',
    
    // DME/Medical Supplies
    dmeCompany: '',
    dmePhone: '',
    oxygenCompany: '',
    oxygenPhone: '',
    communityOrganizations: '',
    communityPhone: '',
    communityContact: '',
    dmeComments: '',
    dmeNoneUsed: false,
    // Wound Care Supplies
    dme2x2s: false,
    dme4x4s: false,
    dmeABDs: false,
    dmeCottonApplicators: false,
    dmeDrainSponges: false,
    dmeTape: false,
    dmeHydrocolloids: false,
    dmeKerlixSize: '',
    dmeNuGauze: false,
    dmeSaline: false,
    dmeTransparentDressings: false,
    dmeWoundCleanser: false,
    dmeWoundGel: false,
    dmeWoundCareOther: '',
    // IV Supplies
    dmeAlcoholSwabs: false,
    dmeAngiocatheterSize: '',
    dmeBatteriesSize: '',
    dmeCentralLineDressing: false,
    dmeExtensionTubings: false,
    dmeInfusionPump: false,
    dmeInjectionCaps: false,
    dmeIVPole: false,
    dmeIVStartKit: false,
    dmeIVTubing: false,
    dmeSyringesSize: '',
    // Urinary/Ostomy
    dmeOstomyPouch: '',
    dmeExternalCatheters: false,
    dmeUrinaryTape: false,
    dmeIrrigationTray: false,
    dmeUrineSaline: false,
    dmeStraightCatheter: false,
    dmeUrinaryOther: '',
    // Catheter Supplies
    dmeAceticAcid: false,
    dmeFrCatheterKit: '',
    // Diabetic
    dmeDiabeticChemstrips: false,
    dmeDiabeticSyringes: false,
    dmeDiabeticOther: '',
    // Miscellaneous
    dmeEnemaSupplies: false,
    dmeFeedingTubeType: '',
    dmeFeedingTubeSize: '',
    dmeGlovesSterile: false,
    dmeGlovesNonSterile: false,
    dmeMedBox: false,
    dmeStapleRemovalKit: false,
    dmeSterStrips: false,
    dmeSutureRemovalKit: false,
    dmeMiscOther: '',
    // Equipment
    dmeAugmentativeDevice: '',
    dmeBathBench: false,
    dmeBraceOrthotics: '',
    dmeCaneEquipment: false,
    dmeCommode: false,
    dmeDressingAidKit: false,
    dmeEggcrate: false,
    dmeEnteralFeedingPump: false,
    dmeGrabBars: false,
    dmeOxygenConcentrator: false,
    dmePressureRelievingDevice: false,
    dmeProsthesisRUE: false,
    dmeProsthesisRLE: false,
    dmeProsthesisLUE: false,
    dmeProsthesisLLE: false,
    dmeProsthesisOther: '',
    dmeRaisedToiletSeat: false,
    dmeReacher: false,
    dmeSpecialMattress: false,
    dmeSuctionMachine: false,
    dmeTENSUnit: false,
    dmeTransferBoard: false,
    dmeTransferLift: false,
    dmeVentilator: false,
    dmeWalkerEquipment: false,
    dmeWheelchairEquipment: false,
    dmeOtherSuppliesNeeded: '',
    
    // Physician Verbal Order
    calledToReportFindings: '',
    verbalOrderReceivedForServices: false,
    verbalOrderSignature: { typed: '', audioURL: null, canvasDataURL: null },
    verbalOrderSignatureDate: '',
    verbalOrderSignatureTime: '',
    physicianVerbalOrderSignature: { typed: '', audioURL: null, canvasDataURL: null },
    physicianVerbalOrderDate: '',
    physicianVerbalOrderTime: '',
    
    // Signatures (comprehensive - typed, voice recording, handwritten)
    patientFamilySignature: { typed: '', audioURL: null, canvasDataURL: null },
    patientFamilySignatureDate: '',
    patientFamilySignatureTime: '',
    personCompletingFormSignature: { typed: '', audioURL: null, canvasDataURL: null },
    personCompletingFormDate: '',
    personCompletingFormTime: '',
    agencyName: '',
    agencyPhoneNumber: '',
    
    // Section C - Cognitive Patterns (BIMS)
    c0100ShouldBIMSBeConducted: '',
    c0200RepetitionOfThreeWords: '',
    c0300AYear: '',
    c0300BMonth: '',
    c0300CDay: '',
    c0400ASock: '',
    c0400BBlue: '',
    c0400CBed: '',
    c0500BIMSSummaryScore: '',
    c1310AAcuteOnset: '',
    c1310BInattention: '',
    c1310CDisorganizedThinking: '',
    c1310DAlteredConsciousness: '',
    m1700CognitiveFunctioning: '',
    
    // Section D - Mood (PHQ-2 to 9)
    d0150ALittleInterest: '',
    d0150BFeelingDown: '',
    d0150CTroubleSleeping: '',
    d0150DFeelingTired: '',
    d0150EPoorAppetite: '',
    d0150FFeelingBad: '',
    d0150GTroubleConcentrating: '',
    d0150HMovingSlowly: '',
    d0150IThoughtsOfHarm: '',
    d0160TotalSeverityScore: '',
    d0700SocialIsolation: '',
    phq2TotalScore: '',
    
    // Section GG - Functional Abilities
    gg0130AEating: '',
    gg0130AEatingGoal: '',
    gg0130BOralHygiene: '',
    gg0130BOralHygieneGoal: '',
    gg0130CToiletingHygiene: '',
    gg0130CToiletingHygieneGoal: '',
    gg0130E1ShowerBathe: '',
    gg0130E1ShowerBatheGoal: '',
    gg0130FUpperDressing: '',
    gg0130FUpperDressingGoal: '',
    gg0130GLowerDressing: '',
    gg0130GLowerDressingGoal: '',
    gg0130HFootwear: '',
    gg0130HFootwearGoal: '',
    gg0170ARoll: '',
    gg0170ARollGoal: '',
    gg0170BSitToLying: '',
    gg0170BSitToLyingGoal: '',
    gg0170CLyingToSitting: '',
    gg0170CLyingToSittingGoal: '',
    gg0170DSitToStand: '',
    gg0170DSitToStandGoal: '',
    gg0170EChairTransfer: '',
    gg0170EChairTransferGoal: '',
    gg0170FToiletTransfer: '',
    gg0170FToiletTransferGoal: '',
    gg0170GCarTransfer: '',
    gg0170GCarTransferGoal: '',
    gg0170IWalk10Feet: '',
    gg0170IWalk10FeetGoal: '',
    gg0170JWalk50Feet: '',
    gg0170JWalk50FeetGoal: '',
    gg0170KWalk150Feet: '',
    gg0170KWalk150FeetGoal: '',
    gg0170LWalkUneven: '',
    gg0170LWalkUnevenGoal: '',
    gg0170M1Step: '',
    gg0170M1StepGoal: '',
    gg0170N4Steps: '',
    gg0170N4StepsGoal: '',
    gg0170O12Steps: '',
    gg0170O12StepsGoal: '',
    gg0170PPickingUp: '',
    gg0170PPickingUpGoal: '',
    gg0170QUsesWheelchair: '',
    gg0170RRWheel50Feet: '',
    gg0170RRWheel50FeetGoal: '',
    gg0170SSWheel150Feet: '',
    gg0170SSWheel150FeetGoal: '',
    functionalLimitationsPlan: '',
    
    // Section J - Health Conditions (Pain)
    j0510PainEffectSleep: '',
    j0520PainInterference: '',
    j0530PainInterferenceActivities: '',
    j1800FallsSinceSOCROC: '',
    j1900ANoInjury: '',
    j1900BInjury: '',
    j1900CMajorInjury: '',
    m1400ShortOfBreath: '',
    painExperiencing: '',
    painLocation: '',
    painPresentLevel: '',
    painWorstLevel: '',
    painBestLevel: '',
    painDescription: '',
    painManagementPlan: '',
    fallsAdditionalComments: '',
    
    // Section M - Skin Conditions
    m1306UnhealedPressureUlcer: '',
    m1307OldestStage2: '',
    m1307Stage2Date: '',
    m1311A1Stage2Number: '',
    m1311A2Stage2AtSOCROC: '',
    m1311B1Stage3Number: '',
    m1311B2Stage3AtSOCROC: '',
    m1311C1Stage4Number: '',
    m1311C2Stage4AtSOCROC: '',
    m1311D1UnstageableDressing: '',
    m1311D2UnstageableDressingAtSOCROC: '',
    m1311E1UnstageableSlough: '',
    m1311E2UnstageableSloughAtSOCROC: '',
    m1311F1DeepTissueInjury: '',
    m1311F2DeepTissueInjuryAtSOCROC: '',
    m1330StasisUlcer: '',
    m1334StasisUlcerStatus: '',
    m1340SurgicalWound: '',
    m1342SurgicalWoundStatus: '',
    woundCareNotes: '',
  });

  const sections = [
    { id: 0, title: 'Section A', description: 'Administrative Information' },
    { id: 1, title: 'Contacts', description: 'Patient Contacts/Caregivers' },
    { id: 2, title: 'Advance Directives', description: 'Advance Directives & Sensory Status' },
    { id: 3, title: 'Neuro/Cognitive', description: 'Neurological & Cognitive Status' },
    { id: 4, title: 'Mental/Psychosocial', description: 'Mental Status & Psychosocial' },
    { id: 5, title: 'Goals/Safety', description: 'Care Preferences, Goals & Safety' },
    { id: 6, title: 'Section G', description: 'Functional Status' },
    { id: 7, title: 'Section GG', description: 'Functional Abilities & Goals' },
    { id: 8, title: 'Pain/Cardio', description: 'Pain & Cardiopulmonary' },
    { id: 9, title: 'Vitals/Nutrition', description: 'Vital Signs & Nutrition' },
    { id: 10, title: 'Section M', description: 'Skin Conditions' },
    { id: 11, title: 'Medications', description: 'Medications & Infusion' },
    { id: 12, title: 'Education', description: 'Patient/Caregiver Education' },
    { id: 13, title: 'Summary', description: 'Recertification Summary' },
    { id: 14, title: 'DME/Signatures', description: 'DME, Supplies & Signatures' },
  ];

  useEffect(() => {
    fetchPatients();
    if (formId) fetchForm();
  }, [formId]);

  useEffect(() => {
    if (!appointmentId) return;
    const token = localStorage.getItem('ivory_user') ? JSON.parse(localStorage.getItem('ivory_user')).token : localStorage.getItem('token');
    fetch(`${API_URL}/api/recert/visit/${appointmentId}`, { headers: { 'Authorization': `Bearer ${token}` } })
      .then(r => r.json())
      .then(data => {
        if (!data.success) return;
        setVisitInfo(data);
        if (!formId) {
          setFormData(prev => ({ ...prev, ...data.prefill }));
          toast.success(`Recert form pre-filled for ${data.appointment.patientName}`);
        }
      })
      .catch(() => toast.error('Could not load the recert visit details'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointmentId]);

  // Auto-calculate BIMS Summary Score (C0500)
  useEffect(() => {
    const c0200 = parseInt(formData.c0200RepetitionOfThreeWords) || 0;
    const c0300A = parseInt(formData.c0300AYear) || 0;
    const c0300B = parseInt(formData.c0300BMonth) || 0;
    const c0300C = parseInt(formData.c0300CDay) || 0;
    const c0400A = parseInt(formData.c0400ASock) || 0;
    const c0400B = parseInt(formData.c0400BBlue) || 0;
    const c0400C = parseInt(formData.c0400CBed) || 0;
    
    const total = c0200 + c0300A + c0300B + c0300C + c0400A + c0400B + c0400C;
    if (formData.c0200RepetitionOfThreeWords || formData.c0300AYear) {
      setFormData(prev => ({ ...prev, c0500BIMSSummaryScore: total.toString().padStart(2, '0') }));
    }
  }, [formData.c0200RepetitionOfThreeWords, formData.c0300AYear, formData.c0300BMonth, formData.c0300CDay, formData.c0400ASock, formData.c0400BBlue, formData.c0400CBed]);

  // Auto-calculate PHQ-2 Score
  useEffect(() => {
    const a = parseInt(formData.d0150ALittleInterest) || 0;
    const b = parseInt(formData.d0150BFeelingDown) || 0;
    const phq2 = a + b;
    if (formData.d0150ALittleInterest || formData.d0150BFeelingDown) {
      setFormData(prev => ({ ...prev, phq2TotalScore: phq2.toString() }));
    }
  }, [formData.d0150ALittleInterest, formData.d0150BFeelingDown]);

  // Auto-calculate PHQ-9 Total Score (D0160)
  useEffect(() => {
    const scores = [
      formData.d0150ALittleInterest, formData.d0150BFeelingDown,
      formData.d0150CTroubleSleeping, formData.d0150DFeelingTired,
      formData.d0150EPoorAppetite, formData.d0150FFeelingBad,
      formData.d0150GTroubleConcentrating, formData.d0150HMovingSlowly,
      formData.d0150IThoughtsOfHarm
    ];
    const total = scores.reduce((sum, val) => sum + (parseInt(val) || 0), 0);
    if (scores.some(s => s !== '' && s !== undefined)) {
      setFormData(prev => ({ ...prev, d0160TotalSeverityScore: total.toString().padStart(2, '0') }));
    }
  }, [formData.d0150ALittleInterest, formData.d0150BFeelingDown, formData.d0150CTroubleSleeping, formData.d0150DFeelingTired, formData.d0150EPoorAppetite, formData.d0150FFeelingBad, formData.d0150GTroubleConcentrating, formData.d0150HMovingSlowly, formData.d0150IThoughtsOfHarm]);

  // Auto-calculate Certification Period End Date (60 days from start)
  useEffect(() => {
    if (formData.certificationPeriodFrom) {
      const startDate = new Date(formData.certificationPeriodFrom);
      if (!isNaN(startDate.getTime())) {
        const endDate = new Date(startDate);
        endDate.setDate(endDate.getDate() + 59); // 60-day period
        const formattedEnd = `${(endDate.getMonth() + 1).toString().padStart(2, '0')}/${endDate.getDate().toString().padStart(2, '0')}/${endDate.getFullYear()}`;
        setFormData(prev => ({ ...prev, certificationPeriodTo: formattedEnd }));
      }
    }
  }, [formData.certificationPeriodFrom]);

  // Auto-calculate Next Recertification Due Date (60 days from current cert end)
  useEffect(() => {
    if (formData.certificationPeriodTo) {
      const certEndDate = new Date(formData.certificationPeriodTo);
      if (!isNaN(certEndDate.getTime())) {
        // Next recert should be done 5 days before cert period ends
        const recertDueDate = new Date(certEndDate);
        recertDueDate.setDate(recertDueDate.getDate() - 5);
        const formattedDue = `${(recertDueDate.getMonth() + 1).toString().padStart(2, '0')}/${recertDueDate.getDate().toString().padStart(2, '0')}/${recertDueDate.getFullYear()}`;
        setFormData(prev => ({ ...prev, nextRecertDueDate: formattedDue }));
      }
    }
  }, [formData.certificationPeriodTo]);

  const fetchPatients = async () => {
    try {
      const token = localStorage.getItem('ivory_user') ? JSON.parse(localStorage.getItem('ivory_user')).token : localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/patients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) setPatients(data.patients);
    } catch (error) {
      console.error('Error fetching patients:', error);
    }
  };

  const fetchForm = async () => {
    try {
      const token = localStorage.getItem('ivory_user') ? JSON.parse(localStorage.getItem('ivory_user')).token : localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/forms/recertification/${formId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success && data.assessment) {
        setFormData(prev => ({ ...prev, ...data.assessment.formData }));
      }
    } catch (error) {
      console.error('Error fetching form:', error);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.patientId) {
      toast.error('Please select a patient');
      return;
    }
    
    setSaving(true);
    try {
      const token = localStorage.getItem('ivory_user') ? JSON.parse(localStorage.getItem('ivory_user')).token : localStorage.getItem('token');
      const url = formId 
        ? `${API_URL}/api/forms/recertification/${formId}`
        : `${API_URL}/api/forms/recertification`;
      
      const selectedPatient = patients.find(p => p.id === formData.patientId);
      
      const payload = {
        patientId: formData.patientId,
        patientName: selectedPatient ? `${selectedPatient.firstName} ${selectedPatient.lastName}` : formData.patientName,
        assessmentType: 'Recertification',
        assessmentDate: formData.m0090DateAssessmentCompleted || new Date().toISOString().split('T')[0],
        formData: formData,
        appointmentId: appointmentId || undefined
      };
      
      const response = await fetch(url, {
        method: formId ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (data.success) {
        toast.success(formId ? 'Form updated' : 'Form created');
        if (!formId && data.assessment?.id) {
          navigate(`/dashboard/forms/recertification-legacy?id=${data.assessment.id}${appointmentId ? `&appointmentId=${appointmentId}` : ''}`);
        }
      } else {
        toast.error(data.detail || 'Failed to save form');
      }
    } catch (error) {
      console.error('Error saving form:', error);
      toast.error('Failed to save form');
    } finally {
      setSaving(false);
    }
  };

  const CheckboxField = ({ label, checked, onChange, className = '' }) => (
    <label className={`flex items-center gap-2 cursor-pointer ${className}`}>
      <input 
        type="checkbox" 
        checked={checked || false} 
        onChange={(e) => onChange(e.target.checked)} 
        className="w-4 h-4 rounded border-gray-300"
      />
      <span className="text-sm">{label}</span>
    </label>
  );

  const renderSectionA = () => (
    <div className="space-y-6">
      <div className="text-center border-b pb-4 mb-6">
        <h2 className="text-xl font-bold text-[#f39c12]">OASIS-E Recertification/Follow-Up Assessment</h2>
        <p className="text-sm text-gray-600">Effective 01/01/2023</p>
      </div>

      {/* Patient Selection */}
      <div className="grid grid-cols-2 gap-4 p-4 bg-blue-50 rounded-lg">
        <div>
          <Label className="font-bold">Patient Name</Label>
          <select
            value={formData.patientId}
            onChange={(e) => {
              const patient = patients.find(p => p.id === e.target.value);
              handleInputChange('patientId', e.target.value);
              if (patient) handleInputChange('patientName', `${patient.firstName} ${patient.lastName}`);
            }}
            className="w-full mt-1 px-3 py-2 border rounded-md"
          >
            <option value="">Select Patient...</option>
            {patients.map(p => (
              <option key={p.id} value={p.id}>{p.firstName} {p.lastName}</option>
            ))}
          </select>
        </div>
        <VoiceDictationInput label="ID #" value={formData.idNumber} onChange={(e) => handleInputChange('idNumber', e.target.value)} />
      </div>

      {/* M0080 - Discipline */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">M0080. Discipline of Person Completing Assessment</h4>
        <div className="flex flex-wrap gap-4">
          {['1. RN', '2. PT', '3. SLP/ST', '4. OT'].map(opt => (
            <CheckboxField 
              key={opt} 
              label={opt} 
              checked={formData.m0080Discipline === opt.split('.')[0]} 
              onChange={() => handleInputChange('m0080Discipline', opt.split('.')[0])} 
            />
          ))}
        </div>
        <div className="grid grid-cols-4 gap-4 mt-4">
          <VoiceDictationInput label="Type of Visit" value={formData.visitType} onChange={(e) => handleInputChange('visitType', e.target.value)} placeholder="Skilled, Other..." />
          <VoiceDictationInput label="TIME IN" value={formData.timeIn} onChange={(e) => handleInputChange('timeIn', e.target.value)} />
          <VoiceDictationInput label="TIME OUT" value={formData.timeOut} onChange={(e) => handleInputChange('timeOut', e.target.value)} />
          <VoiceDictationInput label="DATE" value={formData.visitDate} onChange={(e) => handleInputChange('visitDate', e.target.value)} placeholder="MM/DD/YYYY" />
        </div>
      </div>

      {/* Certification Period - AUTO-CALCULATED */}
      <div className="p-4 border rounded-lg bg-green-50 border-green-300">
        <h4 className="font-bold mb-3 flex items-center gap-2">
          Certification Period
          <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded">AUTO-CALCULATED</span>
        </h4>
        <div className="grid grid-cols-3 gap-4">
          <div>
            <Label className="text-sm font-medium">Certification From:</Label>
            <VoiceDictationInput value={formData.certificationPeriodFrom} onChange={(e) => handleInputChange('certificationPeriodFrom', e.target.value)} placeholder="MM/DD/YYYY" />
          </div>
          <div>
            <Label className="text-sm font-medium">Certification To (60 days):</Label>
            <input type="text" value={formData.certificationPeriodTo} readOnly className="w-full p-2 border rounded bg-green-100 font-medium" />
          </div>
          <div>
            <Label className="text-sm font-medium">Next Recert Due (5 days before):</Label>
            <input type="text" value={formData.nextRecertDueDate || ''} readOnly className="w-full p-2 border rounded bg-yellow-100 font-medium" />
          </div>
        </div>
        <p className="text-xs text-gray-600 mt-2">* Certification period is automatically calculated as 60 days. Recertification due date is 5 days before certification ends.</p>
      </div>

      {/* M0090 */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">M0090. Date Assessment Completed</h4>
        <p className="text-sm text-gray-600 mb-2">Complete M0090 using the date of the day information was last collected.</p>
        <VoiceDictationInput value={formData.m0090DateAssessmentCompleted} onChange={(e) => handleInputChange('m0090DateAssessmentCompleted', e.target.value)} placeholder="MM/DD/YYYY" />
      </div>

      {/* M0100 */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">M0100. This Assessment is Currently Being Completed for the Following Reason</h4>
        <div className="flex flex-wrap gap-4">
          <CheckboxField label="1. Follow-Up" checked={formData.m0100ReasonForAssessment === '1'} onChange={() => handleInputChange('m0100ReasonForAssessment', '1')} />
          <CheckboxField label="4. Recertification (follow-up) reassessment" checked={formData.m0100ReasonForAssessment === '4'} onChange={() => handleInputChange('m0100ReasonForAssessment', '4')} />
          <CheckboxField label="5. Other follow-up" checked={formData.m0100ReasonForAssessment === '5'} onChange={() => handleInputChange('m0100ReasonForAssessment', '5')} />
        </div>
        {formData.m0100ReasonForAssessment === '5' && (
          <VoiceDictationInput label="If coded 5, explain reason:" value={formData.m0100ExplainOther} onChange={(e) => handleInputChange('m0100ExplainOther', e.target.value)} className="mt-2" />
        )}
      </div>

      {/* M0110 */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">M0110. Episode Timing</h4>
        <p className="text-sm text-gray-600 mb-2">Is the Medicare home health payment episode, for which this assessment will define a case mix group, an "early" episode or a "later" episode in the patient's current sequence of adjacent Medicare home health payment episodes?</p>
        <div className="flex flex-wrap gap-4">
          <CheckboxField label="1. Early" checked={formData.m0110EpisodeTiming === '1'} onChange={() => handleInputChange('m0110EpisodeTiming', '1')} />
          <CheckboxField label="2. Later" checked={formData.m0110EpisodeTiming === '2'} onChange={() => handleInputChange('m0110EpisodeTiming', '2')} />
          <CheckboxField label="UK. Unknown" checked={formData.m0110EpisodeTiming === 'UK'} onChange={() => handleInputChange('m0110EpisodeTiming', 'UK')} />
          <CheckboxField label="NA. Not Applicable" checked={formData.m0110EpisodeTiming === 'NA'} onChange={() => handleInputChange('m0110EpisodeTiming', 'NA')} />
        </div>
      </div>
    </div>
  );

  // Section C - Cognitive Patterns (BIMS) - Mapped to Neuro/Cognitive (section 3)
  const renderSectionC = () => (
    <div className="space-y-6">
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Neurological & Cognitive Status</h3>
        
        <div className="bg-yellow-50 p-4 rounded border mb-4">
          <Label className="font-bold text-sm">Brief Interview for Mental Status (BIMS)</Label>
          <p className="text-xs text-gray-600 mt-1">Attempt to conduct interview with all patients.</p>
        </div>

        {/* C0100 */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">C0100. Should BIMS Be Conducted?</Label>
          <div className="flex gap-4 mt-2">
            <label className="flex items-center gap-2">
              <input type="radio" name="c0100" checked={formData.c0100ShouldBIMSBeConducted === '0'} onChange={() => handleInputChange('c0100ShouldBIMSBeConducted', '0')} className="w-4 h-4" />
              <span className="text-sm">0. No</span>
            </label>
            <label className="flex items-center gap-2">
              <input type="radio" name="c0100" checked={formData.c0100ShouldBIMSBeConducted === '1'} onChange={() => handleInputChange('c0100ShouldBIMSBeConducted', '1')} className="w-4 h-4" />
              <span className="text-sm">1. Yes</span>
            </label>
          </div>
        </div>

        {/* C0200-C0500 */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">C0200. Repetition of Three Words (0-3)</Label>
          <VoiceDictationInput value={formData.c0200RepetitionOfThreeWords} onChange={(e) => handleInputChange('c0200RepetitionOfThreeWords', e.target.value)} placeholder="0-3" className="w-32 mt-2" />
        </div>

        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">C0300. Temporal Orientation</Label>
          <div className="grid grid-cols-3 gap-4 mt-2">
            <VoiceDictationInput label="A. Year (0-3)" value={formData.c0300AYear} onChange={(e) => handleInputChange('c0300AYear', e.target.value)} placeholder="0-3" />
            <VoiceDictationInput label="B. Month (0-2)" value={formData.c0300BMonth} onChange={(e) => handleInputChange('c0300BMonth', e.target.value)} placeholder="0-2" />
            <VoiceDictationInput label="C. Day (0-1)" value={formData.c0300CDay} onChange={(e) => handleInputChange('c0300CDay', e.target.value)} placeholder="0-1" />
          </div>
        </div>

        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">C0400. Recall (0-2 each)</Label>
          <div className="grid grid-cols-3 gap-4 mt-2">
            <VoiceDictationInput label="A. Sock" value={formData.c0400ASock} onChange={(e) => handleInputChange('c0400ASock', e.target.value)} placeholder="0-2" />
            <VoiceDictationInput label="B. Blue" value={formData.c0400BBlue} onChange={(e) => handleInputChange('c0400BBlue', e.target.value)} placeholder="0-2" />
            <VoiceDictationInput label="C. Bed" value={formData.c0400CBed} onChange={(e) => handleInputChange('c0400CBed', e.target.value)} placeholder="0-2" />
          </div>
        </div>

        {/* C0500 BIMS Summary Score - AUTO-CALCULATED */}
        <div className="bg-green-50 p-4 rounded border mb-4 border-green-300">
          <Label className="font-bold text-sm flex items-center gap-2">
            C0500. BIMS Summary Score
            <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded">AUTO-CALCULATED</span>
          </Label>
          <p className="text-xs text-gray-600 mb-2">C0200 + C0300A + C0300B + C0300C + C0400A + C0400B + C0400C</p>
          <div className="flex items-center gap-4">
            <input type="text" value={formData.c0500BIMSSummaryScore} readOnly className="w-20 p-2 border rounded bg-green-100 font-bold text-lg text-center" />
            <span className="text-sm text-gray-600">
              {parseInt(formData.c0500BIMSSummaryScore) >= 13 ? '✅ Intact (13-15)' :
               parseInt(formData.c0500BIMSSummaryScore) >= 8 ? '⚠️ Moderate (8-12)' :
               parseInt(formData.c0500BIMSSummaryScore) >= 0 ? '🔴 Severe (0-7)' : ''}
            </span>
          </div>
        </div>

        {/* C1310 CAM */}
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">C1310. Signs and Symptoms of Delirium (CAM©)</Label>
          <div className="grid grid-cols-2 gap-4 mt-3">
            <VoiceDictationInput label="A. Acute Onset (0/1)" value={formData.c1310AAcuteOnset} onChange={(e) => handleInputChange('c1310AAcuteOnset', e.target.value)} placeholder="0 or 1" />
            <VoiceDictationInput label="B. Inattention (0/1)" value={formData.c1310BInattention} onChange={(e) => handleInputChange('c1310BInattention', e.target.value)} placeholder="0 or 1" />
            <VoiceDictationInput label="C. Disorganized Thinking (0/1)" value={formData.c1310CDisorganizedThinking} onChange={(e) => handleInputChange('c1310CDisorganizedThinking', e.target.value)} placeholder="0 or 1" />
            <VoiceDictationInput label="D. Altered Consciousness (0/1)" value={formData.c1310DAlteredConsciousness} onChange={(e) => handleInputChange('c1310DAlteredConsciousness', e.target.value)} placeholder="0 or 1" />
          </div>
        </div>

        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1700. Cognitive Functioning (0-4)</Label>
          <div className="text-xs text-gray-600 mt-1 mb-2">0=Alert/oriented, 1=Requires prompting, 2=Difficulty with memory, 3=Severely impaired, 4=Totally dependent</div>
          <VoiceDictationInput value={formData.m1700CognitiveFunctioning} onChange={(e) => handleInputChange('m1700CognitiveFunctioning', e.target.value)} placeholder="0-4" className="w-32" />
        </div>
      </div>
    </div>
  );

  // Section D - Mood (PHQ-2 to PHQ-9) - Mapped to Mental/Psychosocial (section 4)
  const renderSectionD = () => (
    <div className="space-y-6">
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Mental Status & Psychosocial</h3>
        
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">D0150. Patient Mood Interview (PHQ-2 to PHQ-9©)</Label>
          <p className="text-xs text-gray-600 mb-3">"Over the last 2 weeks, have you been bothered by..." (0=Not at all, 1=Several days, 2=More than half, 3=Nearly every day)</p>
          
          <div className="space-y-3">
            {[
              {key: 'd0150ALittleInterest', label: 'A. Little interest or pleasure'},
              {key: 'd0150BFeelingDown', label: 'B. Feeling down, depressed, or hopeless'},
              {key: 'd0150CTroubleSleeping', label: 'C. Trouble sleeping'},
              {key: 'd0150DFeelingTired', label: 'D. Feeling tired'},
              {key: 'd0150EPoorAppetite', label: 'E. Poor appetite or overeating'},
              {key: 'd0150FFeelingBad', label: 'F. Feeling bad about yourself'},
              {key: 'd0150GTroubleConcentrating', label: 'G. Trouble concentrating'},
              {key: 'd0150HMovingSlowly', label: 'H. Moving or speaking slowly'},
              {key: 'd0150IThoughtsOfHarm', label: 'I. Thoughts of self-harm'},
            ].map((item) => (
              <div key={item.key} className="flex items-center justify-between border-b pb-2">
                <span className="text-sm">{item.label}</span>
                <div className="flex gap-2">
                  {['0','1','2','3'].map(v => (
                    <label key={v} className="flex items-center gap-1">
                      <input type="radio" name={item.key} checked={formData[item.key] === v} onChange={() => handleInputChange(item.key, v)} className="w-3 h-3" />
                      <span className="text-xs">{v}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
          
          <div className="grid grid-cols-2 gap-4 mt-4 p-3 bg-green-50 rounded border border-green-300">
            <div>
              <Label className="text-sm font-medium flex items-center gap-2">
                PHQ-2 Score (A+B)
                <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded">AUTO</span>
              </Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="text" value={formData.phq2TotalScore} readOnly className="w-16 p-2 border rounded bg-green-100 font-bold text-center" />
                <span className="text-xs">{parseInt(formData.phq2TotalScore) >= 3 ? '🔴 ≥3' : '✅ <3'}</span>
              </div>
            </div>
            <div>
              <Label className="text-sm font-medium flex items-center gap-2">
                D0160. Total Score (A-I)
                <span className="text-xs bg-green-500 text-white px-2 py-0.5 rounded">AUTO</span>
              </Label>
              <div className="flex items-center gap-2 mt-1">
                <input type="text" value={formData.d0160TotalSeverityScore} readOnly className="w-16 p-2 border rounded bg-green-100 font-bold text-center" />
                <span className="text-xs">
                  {parseInt(formData.d0160TotalSeverityScore) >= 20 ? '🔴 Severe' :
                   parseInt(formData.d0160TotalSeverityScore) >= 10 ? '🟡 Moderate' :
                   parseInt(formData.d0160TotalSeverityScore) >= 0 ? '✅ Minimal' : ''}
                </span>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">D0700. Social Isolation (0-4, 9=Unable)</Label>
          <p className="text-xs text-gray-600 mt-1 mb-2">"How often do you feel lonely or isolated?" 0=Never, 1=Rarely, 2=Sometimes, 3=Often, 4=Always</p>
          <VoiceDictationInput value={formData.d0700SocialIsolation} onChange={(e) => handleInputChange('d0700SocialIsolation', e.target.value)} placeholder="0-4 or 9" className="w-32" />
        </div>
      </div>
    </div>
  );

  // Section GG - Functional Abilities - Mapped to section 7
  const renderSectionGG = () => (
    <div className="space-y-6">
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Section GG - Functional Abilities & Goals</h3>
        
        <div className="bg-yellow-50 p-4 rounded border mb-4">
          <Label className="font-bold text-sm">Coding Guide</Label>
          <p className="text-xs text-gray-600 mt-1">06=Independent, 05=Setup, 04=Supervision, 03=Partial, 02=Substantial, 01=Dependent, 07=Refused, 09=N/A, 88=Not attempted</p>
        </div>

        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">GG0130. Self-Care</Label>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-sm border">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left">Activity</th>
                  <th className="border p-2 text-center">Score</th>
                  <th className="border p-2 text-center">Goal</th>
                </tr>
              </thead>
              <tbody>
                {[
                  {key: 'gg0130AEating', label: 'A. Eating'},
                  {key: 'gg0130BOralHygiene', label: 'B. Oral Hygiene'},
                  {key: 'gg0130CToiletingHygiene', label: 'C. Toileting Hygiene'},
                  {key: 'gg0130E1ShowerBathe', label: 'E1. Shower/Bathe'},
                  {key: 'gg0130FUpperDressing', label: 'F. Upper Dressing'},
                  {key: 'gg0130GLowerDressing', label: 'G. Lower Dressing'},
                  {key: 'gg0130HFootwear', label: 'H. Footwear'},
                ].map((item) => (
                  <tr key={item.key}>
                    <td className="border p-2">{item.label}</td>
                    <td className="border p-2"><VoiceDictationInput value={formData[item.key]} onChange={(e) => handleInputChange(item.key, e.target.value)} placeholder="" className="w-14" /></td>
                    <td className="border p-2"><VoiceDictationInput value={formData[`${item.key}Goal`]} onChange={(e) => handleInputChange(`${item.key}Goal`, e.target.value)} placeholder="" className="w-14" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">GG0170. Mobility</Label>
          <div className="overflow-x-auto mt-2">
            <table className="w-full text-sm border">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border p-2 text-left">Activity</th>
                  <th className="border p-2 text-center">Score</th>
                  <th className="border p-2 text-center">Goal</th>
                </tr>
              </thead>
              <tbody>
                {[
                  {key: 'gg0170ARoll', label: 'A. Roll'},
                  {key: 'gg0170BSitToLying', label: 'B. Sit to lying'},
                  {key: 'gg0170CLyingToSitting', label: 'C. Lying to sitting'},
                  {key: 'gg0170DSitToStand', label: 'D. Sit to stand'},
                  {key: 'gg0170EChairTransfer', label: 'E. Chair transfer'},
                  {key: 'gg0170FToiletTransfer', label: 'F. Toilet transfer'},
                  {key: 'gg0170IWalk10Feet', label: 'I. Walk 10 feet'},
                  {key: 'gg0170JWalk50Feet', label: 'J. Walk 50 feet'},
                  {key: 'gg0170KWalk150Feet', label: 'K. Walk 150 feet'},
                  {key: 'gg0170M1Step', label: 'M. 1 step'},
                  {key: 'gg0170N4Steps', label: 'N. 4 steps'},
                  {key: 'gg0170O12Steps', label: 'O. 12 steps'},
                ].map((item) => (
                  <tr key={item.key}>
                    <td className="border p-2 text-sm">{item.label}</td>
                    <td className="border p-2"><VoiceDictationInput value={formData[item.key]} onChange={(e) => handleInputChange(item.key, e.target.value)} placeholder="" className="w-14" /></td>
                    <td className="border p-2"><VoiceDictationInput value={formData[`${item.key}Goal`]} onChange={(e) => handleInputChange(`${item.key}Goal`, e.target.value)} placeholder="" className="w-14" /></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-gray-50 p-4 rounded border">
          <VoiceDictationInput label="Functional Limitations Plan" value={formData.functionalLimitationsPlan} onChange={(e) => handleInputChange('functionalLimitationsPlan', e.target.value)} rows={3} placeholder="Document functional limitations and plan..." />
        </div>
      </div>
    </div>
  );

  // Section J - Health Conditions (Pain & Falls) - Mapped to Pain/Cardio (section 8)
  const renderSectionJ = () => (
    <div className="space-y-6">
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Pain & Cardiopulmonary</h3>
        
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">Pain Assessment</Label>
          <div className="mt-3">
            <Label className="text-sm">Is patient currently experiencing pain?</Label>
            <div className="flex gap-4 mt-1">
              <label className="flex items-center gap-2"><input type="radio" name="painExp" checked={formData.painExperiencing === '0'} onChange={() => handleInputChange('painExperiencing', '0')} /><span className="text-sm">0. No</span></label>
              <label className="flex items-center gap-2"><input type="radio" name="painExp" checked={formData.painExperiencing === '1'} onChange={() => handleInputChange('painExperiencing', '1')} /><span className="text-sm">1. Yes</span></label>
            </div>
          </div>
          {formData.painExperiencing === '1' && (
            <div className="mt-4 p-4 bg-gray-50 rounded space-y-3">
              <VoiceDictationInput label="Pain Location(s)" value={formData.painLocation} onChange={(e) => handleInputChange('painLocation', e.target.value)} />
              <div className="grid grid-cols-3 gap-4">
                <VoiceDictationInput label="Current (0-10)" value={formData.painPresentLevel} onChange={(e) => handleInputChange('painPresentLevel', e.target.value)} placeholder="0-10" />
                <VoiceDictationInput label="Worst (0-10)" value={formData.painWorstLevel} onChange={(e) => handleInputChange('painWorstLevel', e.target.value)} placeholder="0-10" />
                <VoiceDictationInput label="Best (0-10)" value={formData.painBestLevel} onChange={(e) => handleInputChange('painBestLevel', e.target.value)} placeholder="0-10" />
              </div>
              <VoiceDictationInput label="Pain Description" value={formData.painDescription} onChange={(e) => handleInputChange('painDescription', e.target.value)} />
            </div>
          )}
        </div>

        <div className="bg-white p-4 rounded border mb-4">
          <div className="grid grid-cols-3 gap-4">
            <VoiceDictationInput label="J0510. Pain Effect on Sleep (0-4, 8, 9)" value={formData.j0510PainEffectSleep} onChange={(e) => handleInputChange('j0510PainEffectSleep', e.target.value)} placeholder="0-4, 8, 9" />
            <VoiceDictationInput label="J0520. Pain Interference - Therapy" value={formData.j0520PainInterference} onChange={(e) => handleInputChange('j0520PainInterference', e.target.value)} placeholder="0-4, 8, 9" />
            <VoiceDictationInput label="J0530. Pain Interference - Activities" value={formData.j0530PainInterferenceActivities} onChange={(e) => handleInputChange('j0530PainInterferenceActivities', e.target.value)} placeholder="0-4, 8, 9" />
          </div>
        </div>

        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">Falls Assessment</Label>
          <div className="mt-3">
            <VoiceDictationInput label="J1800. Falls Since SOC/ROC (0, 1, 9)" value={formData.j1800FallsSinceSOCROC} onChange={(e) => handleInputChange('j1800FallsSinceSOCROC', e.target.value)} placeholder="0, 1, or 9" className="w-24" />
          </div>
          {formData.j1800FallsSinceSOCROC === '1' && (
            <div className="mt-3 grid grid-cols-3 gap-4 p-3 bg-yellow-50 rounded">
              <VoiceDictationInput label="J1900A. No injury" value={formData.j1900ANoInjury} onChange={(e) => handleInputChange('j1900ANoInjury', e.target.value)} placeholder="0, 1, 2+" />
              <VoiceDictationInput label="J1900B. Injury" value={formData.j1900BInjury} onChange={(e) => handleInputChange('j1900BInjury', e.target.value)} placeholder="0, 1, 2+" />
              <VoiceDictationInput label="J1900C. Major injury" value={formData.j1900CMajorInjury} onChange={(e) => handleInputChange('j1900CMajorInjury', e.target.value)} placeholder="0, 1, 2+" />
            </div>
          )}
        </div>

        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1400. Dyspnea/Shortness of Breath (0-4)</Label>
          <p className="text-xs text-gray-600 mt-1 mb-2">0=Not SOB, 1=Walking &gt;20 ft, 2=Moderate exertion, 3=Minimal exertion, 4=At rest</p>
          <VoiceDictationInput value={formData.m1400ShortOfBreath} onChange={(e) => handleInputChange('m1400ShortOfBreath', e.target.value)} placeholder="0-4" className="w-32" />
        </div>

        <div className="bg-gray-50 p-4 rounded border">
          <VoiceDictationInput label="Pain Management Plan" value={formData.painManagementPlan} onChange={(e) => handleInputChange('painManagementPlan', e.target.value)} rows={3} placeholder="Document pain management interventions..." />
        </div>
      </div>
    </div>
  );

  // Section M - Skin Conditions - Mapped to section 10
  const renderSectionM = () => (
    <div className="space-y-6">
      <div className="border-t-4 border-[#ea580c] pt-4">
        <h3 className="text-lg font-bold mb-4 text-[#ea580c]">Section M - Skin Conditions</h3>
        
        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1306. Unhealed Pressure Ulcer at Stage 2 or Higher?</Label>
          <div className="flex gap-4 mt-2">
            <label className="flex items-center gap-2"><input type="radio" name="m1306" checked={formData.m1306UnhealedPressureUlcer === '0'} onChange={() => handleInputChange('m1306UnhealedPressureUlcer', '0')} /><span className="text-sm">0. No</span></label>
            <label className="flex items-center gap-2"><input type="radio" name="m1306" checked={formData.m1306UnhealedPressureUlcer === '1'} onChange={() => handleInputChange('m1306UnhealedPressureUlcer', '1')} /><span className="text-sm">1. Yes</span></label>
          </div>
        </div>

        {formData.m1306UnhealedPressureUlcer === '1' && (
          <div className="bg-white p-4 rounded border mb-4">
            <Label className="font-bold text-sm">M1311. Current Number of Pressure Ulcers</Label>
            <div className="grid grid-cols-3 gap-4 mt-3">
              <VoiceDictationInput label="A. Stage 2 (#)" value={formData.m1311A1Stage2Number} onChange={(e) => handleInputChange('m1311A1Stage2Number', e.target.value)} placeholder="0-4" />
              <VoiceDictationInput label="B. Stage 3 (#)" value={formData.m1311B1Stage3Number} onChange={(e) => handleInputChange('m1311B1Stage3Number', e.target.value)} placeholder="0-4" />
              <VoiceDictationInput label="C. Stage 4 (#)" value={formData.m1311C1Stage4Number} onChange={(e) => handleInputChange('m1311C1Stage4Number', e.target.value)} placeholder="0-4" />
              <VoiceDictationInput label="D. Unstageable/Dressing" value={formData.m1311D1UnstageableDressing} onChange={(e) => handleInputChange('m1311D1UnstageableDressing', e.target.value)} placeholder="0-4" />
              <VoiceDictationInput label="E. Unstageable/Slough" value={formData.m1311E1UnstageableSlough} onChange={(e) => handleInputChange('m1311E1UnstageableSlough', e.target.value)} placeholder="0-4" />
              <VoiceDictationInput label="F. Deep Tissue Injury" value={formData.m1311F1DeepTissueInjury} onChange={(e) => handleInputChange('m1311F1DeepTissueInjury', e.target.value)} placeholder="0-4" />
            </div>
          </div>
        )}

        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1330. Stasis Ulcer (0, 1, or 2)</Label>
          <VoiceDictationInput value={formData.m1330StasisUlcer} onChange={(e) => handleInputChange('m1330StasisUlcer', e.target.value)} placeholder="0, 1, or 2" className="w-24 mt-2" />
          {(formData.m1330StasisUlcer === '1' || formData.m1330StasisUlcer === '2') && (
            <div className="mt-2">
              <VoiceDictationInput label="M1334. Status of Most Problematic (0-3)" value={formData.m1334StasisUlcerStatus} onChange={(e) => handleInputChange('m1334StasisUlcerStatus', e.target.value)} placeholder="0-3" className="w-24" />
            </div>
          )}
        </div>

        <div className="bg-white p-4 rounded border mb-4">
          <Label className="font-bold text-sm">M1340. Surgical Wound (0, 1, or 2)</Label>
          <VoiceDictationInput value={formData.m1340SurgicalWound} onChange={(e) => handleInputChange('m1340SurgicalWound', e.target.value)} placeholder="0, 1, or 2" className="w-24 mt-2" />
          {(formData.m1340SurgicalWound === '1' || formData.m1340SurgicalWound === '2') && (
            <div className="mt-2">
              <VoiceDictationInput label="M1342. Status of Most Problematic (0-3)" value={formData.m1342SurgicalWoundStatus} onChange={(e) => handleInputChange('m1342SurgicalWoundStatus', e.target.value)} placeholder="0-3" className="w-24" />
            </div>
          )}
        </div>

        <div className="bg-gray-50 p-4 rounded border">
          <VoiceDictationInput label="Wound Care Notes" value={formData.woundCareNotes} onChange={(e) => handleInputChange('woundCareNotes', e.target.value)} rows={4} placeholder="Document wound care interventions and outcomes..." />
        </div>
      </div>
    </div>
  );

  // Section for Contacts/Caregivers
  const renderContacts = () => (
    <div className="space-y-6">
      <div className="text-center border-b pb-4 mb-6">
        <h3 className="text-lg font-bold text-[#f39c12]">Patient Contacts & Caregivers</h3>
      </div>

      {/* Emergency Contact */}
      <div className="p-4 border rounded-lg bg-red-50">
        <h4 className="font-bold mb-3 text-red-800">Emergency Contact</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <VoiceDictationInput label="Name" value={formData.emergencyContactName} onChange={(e) => handleInputChange('emergencyContactName', e.target.value)} />
          <VoiceDictationInput label="Relationship" value={formData.emergencyContactRelationship} onChange={(e) => handleInputChange('emergencyContactRelationship', e.target.value)} />
          <VoiceDictationInput label="Phone" value={formData.emergencyContactPhone} onChange={(e) => handleInputChange('emergencyContactPhone', e.target.value)} />
          <VoiceDictationInput label="Alt Phone" value={formData.emergencyContactAltPhone} onChange={(e) => handleInputChange('emergencyContactAltPhone', e.target.value)} />
        </div>
      </div>

      {/* Primary Caregiver */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">Primary Caregiver</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <VoiceDictationInput label="Name" value={formData.primaryCaregiverName} onChange={(e) => handleInputChange('primaryCaregiverName', e.target.value)} />
          <VoiceDictationInput label="Relationship" value={formData.primaryCaregiverRelationship} onChange={(e) => handleInputChange('primaryCaregiverRelationship', e.target.value)} />
          <VoiceDictationInput label="Phone" value={formData.primaryCaregiverPhone} onChange={(e) => handleInputChange('primaryCaregiverPhone', e.target.value)} />
          <VoiceDictationInput label="Email" value={formData.primaryCaregiverEmail} onChange={(e) => handleInputChange('primaryCaregiverEmail', e.target.value)} />
        </div>
        <div className="mt-3">
          <VoiceDictationInput label="Hours Available for Caregiving" value={formData.caregiverHoursAvailable} onChange={(e) => handleInputChange('caregiverHoursAvailable', e.target.value)} />
        </div>
      </div>

      {/* Additional Contact */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">Additional Contact</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <VoiceDictationInput label="Name" value={formData.additionalContactName} onChange={(e) => handleInputChange('additionalContactName', e.target.value)} />
          <VoiceDictationInput label="Relationship" value={formData.additionalContactRelationship} onChange={(e) => handleInputChange('additionalContactRelationship', e.target.value)} />
          <VoiceDictationInput label="Phone" value={formData.additionalContactPhone} onChange={(e) => handleInputChange('additionalContactPhone', e.target.value)} />
        </div>
      </div>

      {/* Caregiver Assessment */}
      <div className="p-4 border rounded-lg bg-blue-50">
        <h4 className="font-bold mb-3 text-blue-800">Caregiver Assessment</h4>
        <div className="space-y-3">
          <CheckboxField label="Caregiver willing and able to assist" checked={formData.caregiverWillingAble} onChange={(v) => handleInputChange('caregiverWillingAble', v)} />
          <CheckboxField label="Caregiver has been trained on care needs" checked={formData.caregiverTrained} onChange={(v) => handleInputChange('caregiverTrained', v)} />
          <CheckboxField label="Caregiver shows signs of stress/burnout" checked={formData.caregiverStress} onChange={(v) => handleInputChange('caregiverStress', v)} />
        </div>
        <VoiceDictationInput 
          label="Caregiver Assessment Notes" 
          value={formData.caregiverAssessmentNotes} 
          onChange={(e) => handleInputChange('caregiverAssessmentNotes', e.target.value)} 
          rows={3}
          className="mt-3"
        />
      </div>
    </div>
  );

  // Advance Directives & Sensory Status
  const renderAdvanceDirectives = () => (
    <div className="space-y-6">
      <div className="text-center border-b pb-4 mb-6">
        <h3 className="text-lg font-bold text-[#f39c12]">Advance Directives & Sensory Status</h3>
      </div>

      {/* M1100 Advance Directives */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">M1100. Advance Directives</h4>
        <p className="text-sm text-gray-600 mb-3">Which advance directives does the patient have? (Check all that apply)</p>
        <div className="grid grid-cols-2 gap-3">
          <CheckboxField label="Living Will" checked={formData.m1100LivingWill} onChange={(v) => handleInputChange('m1100LivingWill', v)} />
          <CheckboxField label="Durable Power of Attorney for Health Care" checked={formData.m1100DPAHC} onChange={(v) => handleInputChange('m1100DPAHC', v)} />
          <CheckboxField label="Do Not Resuscitate (DNR)" checked={formData.m1100DNR} onChange={(v) => handleInputChange('m1100DNR', v)} />
          <CheckboxField label="Do Not Hospitalize (DNH)" checked={formData.m1100DNH} onChange={(v) => handleInputChange('m1100DNH', v)} />
          <CheckboxField label="Other (specify)" checked={formData.m1100Other} onChange={(v) => handleInputChange('m1100Other', v)} />
          <CheckboxField label="None" checked={formData.m1100None} onChange={(v) => handleInputChange('m1100None', v)} />
        </div>
        {formData.m1100Other && (
          <VoiceDictationInput label="Other Directive" value={formData.m1100OtherSpecify} onChange={(e) => handleInputChange('m1100OtherSpecify', e.target.value)} className="mt-3" />
        )}
      </div>

      {/* Sensory Status - Vision */}
      <div className="p-4 border rounded-lg bg-blue-50">
        <h4 className="font-bold mb-3 text-blue-800">B1000. Vision</h4>
        <p className="text-sm text-gray-600 mb-3">Ability to see in adequate light (with glasses if used)</p>
        <div className="space-y-2">
          {[
            { value: '0', label: '0 - Adequate - sees fine detail' },
            { value: '1', label: '1 - Impaired - sees large print' },
            { value: '2', label: '2 - Moderately impaired - limited vision' },
            { value: '3', label: '3 - Highly impaired - object identification questionable' },
            { value: '4', label: '4 - Severely impaired - no vision or sees only light' }
          ].map(opt => (
            <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="b1000Vision" value={opt.value} checked={formData.b1000Vision === opt.value} onChange={(e) => handleInputChange('b1000Vision', e.target.value)} />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Sensory Status - Hearing */}
      <div className="p-4 border rounded-lg bg-green-50">
        <h4 className="font-bold mb-3 text-green-800">B0200. Hearing</h4>
        <p className="text-sm text-gray-600 mb-3">Ability to hear (with hearing aid if used)</p>
        <div className="space-y-2">
          {[
            { value: '0', label: '0 - Adequate - no difficulty in normal conversation' },
            { value: '1', label: '1 - Minimal difficulty - difficulty in some environments' },
            { value: '2', label: '2 - Moderate difficulty - speaker must raise voice' },
            { value: '3', label: '3 - Highly impaired - absence of useful hearing' }
          ].map(opt => (
            <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="b0200Hearing" value={opt.value} checked={formData.b0200Hearing === opt.value} onChange={(e) => handleInputChange('b0200Hearing', e.target.value)} />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Speech */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">B0700. Speech Clarity</h4>
        <div className="space-y-2">
          {[
            { value: '0', label: '0 - Clear speech' },
            { value: '1', label: '1 - Unclear speech' },
            { value: '2', label: '2 - No speech' }
          ].map(opt => (
            <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="b0700SpeechClarity" value={opt.value} checked={formData.b0700SpeechClarity === opt.value} onChange={(e) => handleInputChange('b0700SpeechClarity', e.target.value)} />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );

  // Goals/Safety Section
  const renderGoalsSafety = () => (
    <div className="space-y-6">
      <div className="text-center border-b pb-4 mb-6">
        <h3 className="text-lg font-bold text-[#f39c12]">Care Preferences, Goals & Safety</h3>
      </div>

      {/* Patient Goals */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">Patient Goals for This Certification Period</h4>
        <div className="space-y-3">
          <VoiceDictationInput label="Goal 1" value={formData.recertGoal1} onChange={(e) => handleInputChange('recertGoal1', e.target.value)} placeholder="Enter patient's first goal..." />
          <VoiceDictationInput label="Goal 2" value={formData.recertGoal2} onChange={(e) => handleInputChange('recertGoal2', e.target.value)} placeholder="Enter patient's second goal..." />
          <VoiceDictationInput label="Goal 3" value={formData.recertGoal3} onChange={(e) => handleInputChange('recertGoal3', e.target.value)} placeholder="Enter patient's third goal..." />
        </div>
      </div>

      {/* Safety Assessment */}
      <div className="p-4 border rounded-lg bg-red-50">
        <h4 className="font-bold mb-3 text-red-800">Safety Assessment</h4>
        <div className="grid grid-cols-2 gap-3">
          <CheckboxField label="Fall risk identified" checked={formData.fallRiskIdentified} onChange={(v) => handleInputChange('fallRiskIdentified', v)} />
          <CheckboxField label="Home environment safe" checked={formData.homeEnvironmentSafe} onChange={(v) => handleInputChange('homeEnvironmentSafe', v)} />
          <CheckboxField label="911 access available" checked={formData.access911Available} onChange={(v) => handleInputChange('access911Available', v)} />
          <CheckboxField label="Working phone available" checked={formData.workingPhoneAvailable} onChange={(v) => handleInputChange('workingPhoneAvailable', v)} />
          <CheckboxField label="Smoke detectors present" checked={formData.smokeDetectors} onChange={(v) => handleInputChange('smokeDetectors', v)} />
          <CheckboxField label="Medical alert device" checked={formData.medicalAlertDevice} onChange={(v) => handleInputChange('medicalAlertDevice', v)} />
        </div>
      </div>

      {/* Fall Prevention */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">Fall Prevention Interventions</h4>
        <div className="grid grid-cols-2 gap-3">
          <CheckboxField label="Clear pathways" checked={formData.clearPathways} onChange={(v) => handleInputChange('clearPathways', v)} />
          <CheckboxField label="Adequate lighting" checked={formData.adequateLighting} onChange={(v) => handleInputChange('adequateLighting', v)} />
          <CheckboxField label="Non-slip rugs" checked={formData.nonSlipRugs} onChange={(v) => handleInputChange('nonSlipRugs', v)} />
          <CheckboxField label="Grab bars installed" checked={formData.grabBarsInstalled} onChange={(v) => handleInputChange('grabBarsInstalled', v)} />
          <CheckboxField label="Assistive device used" checked={formData.assistiveDeviceUsed} onChange={(v) => handleInputChange('assistiveDeviceUsed', v)} />
        </div>
        <VoiceDictationInput label="Fall Prevention Notes" value={formData.fallPreventionNotes} onChange={(e) => handleInputChange('fallPreventionNotes', e.target.value)} rows={3} className="mt-3" />
      </div>
    </div>
  );

  // Section G - Functional Status
  const renderSectionG = () => (
    <div className="space-y-6">
      <div className="text-center border-b pb-4 mb-6">
        <h3 className="text-lg font-bold text-[#f39c12]">Section G - Functional Status</h3>
      </div>

      {/* ADL Summary */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">ADL Self-Performance Summary</h4>
        <p className="text-sm text-gray-600 mb-3">Code: 0=Independent, 1=Setup help only, 2=One person physical assist, 3=Two+ person assist, 4=Dependent</p>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { key: 'adlBedMobility', label: 'Bed Mobility' },
            { key: 'adlTransfer', label: 'Transfer' },
            { key: 'adlWalkInRoom', label: 'Walk in Room' },
            { key: 'adlLocomotion', label: 'Locomotion' },
            { key: 'adlDressing', label: 'Dressing' },
            { key: 'adlEating', label: 'Eating' },
            { key: 'adlToileting', label: 'Toilet Use' },
            { key: 'adlPersonalHygiene', label: 'Personal Hygiene' },
            { key: 'adlBathing', label: 'Bathing' }
          ].map(item => (
            <div key={item.key}>
              <Label className="text-sm">{item.label}</Label>
              <select 
                value={formData[item.key] || ''} 
                onChange={(e) => handleInputChange(item.key, e.target.value)}
                className="w-full mt-1 px-2 py-1 border rounded text-sm"
              >
                <option value="">Select...</option>
                <option value="0">0 - Independent</option>
                <option value="1">1 - Setup Help</option>
                <option value="2">2 - One Person Assist</option>
                <option value="3">3 - Two+ Person Assist</option>
                <option value="4">4 - Dependent</option>
              </select>
            </div>
          ))}
        </div>
      </div>

      {/* IADL Summary */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">IADL Summary</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          {[
            { key: 'iadlMealPrep', label: 'Meal Preparation' },
            { key: 'iadlHousework', label: 'Ordinary Housework' },
            { key: 'iadlShoppingGrocery', label: 'Shopping' },
            { key: 'iadlPhoneUse', label: 'Telephone Use' },
            { key: 'iadlMedManagement', label: 'Medication Management' },
            { key: 'iadlFinances', label: 'Finances' }
          ].map(item => (
            <div key={item.key}>
              <Label className="text-sm">{item.label}</Label>
              <select 
                value={formData[item.key] || ''} 
                onChange={(e) => handleInputChange(item.key, e.target.value)}
                className="w-full mt-1 px-2 py-1 border rounded text-sm"
              >
                <option value="">Select...</option>
                <option value="0">0 - Independent</option>
                <option value="1">1 - Independent with setup</option>
                <option value="2">2 - Some assistance needed</option>
                <option value="3">3 - Dependent</option>
                <option value="NA">NA - Not Applicable</option>
              </select>
            </div>
          ))}
        </div>
      </div>

      {/* Functional Notes */}
      <div className="p-4 border rounded-lg">
        <VoiceDictationInput label="Functional Status Notes" value={formData.functionalStatusNotes} onChange={(e) => handleInputChange('functionalStatusNotes', e.target.value)} rows={4} placeholder="Document functional status observations and changes..." />
      </div>
    </div>
  );

  // Vitals/Nutrition Section
  const renderVitalsNutrition = () => (
    <div className="space-y-6">
      <div className="text-center border-b pb-4 mb-6">
        <h3 className="text-lg font-bold text-[#f39c12]">Vital Signs & Nutrition</h3>
      </div>

      {/* Vital Signs */}
      <div className="p-4 border rounded-lg bg-blue-50">
        <h4 className="font-bold mb-3 text-blue-800">Current Vital Signs</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <VoiceDictationInput label="Blood Pressure" value={formData.vitalsBP} onChange={(e) => handleInputChange('vitalsBP', e.target.value)} placeholder="120/80" />
          <VoiceDictationInput label="Heart Rate" value={formData.vitalsHR} onChange={(e) => handleInputChange('vitalsHR', e.target.value)} placeholder="72" />
          <VoiceDictationInput label="Respiratory Rate" value={formData.vitalsRR} onChange={(e) => handleInputChange('vitalsRR', e.target.value)} placeholder="16" />
          <VoiceDictationInput label="Temperature" value={formData.vitalsTemp} onChange={(e) => handleInputChange('vitalsTemp', e.target.value)} placeholder="98.6" />
          <VoiceDictationInput label="O2 Saturation" value={formData.vitalsO2Sat} onChange={(e) => handleInputChange('vitalsO2Sat', e.target.value)} placeholder="98%" />
          <VoiceDictationInput label="Weight (lbs)" value={formData.vitalsWeight} onChange={(e) => handleInputChange('vitalsWeight', e.target.value)} />
          <VoiceDictationInput label="Height" value={formData.vitalsHeight} onChange={(e) => handleInputChange('vitalsHeight', e.target.value)} />
          <VoiceDictationInput label="Pain Level (0-10)" value={formData.vitalsPain} onChange={(e) => handleInputChange('vitalsPain', e.target.value)} />
        </div>
      </div>

      {/* Nutritional Status */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">K0520. Nutritional Approaches</h4>
        <div className="grid grid-cols-2 gap-3">
          <CheckboxField label="Parenteral/IV feeding" checked={formData.k0520Parenteral} onChange={(v) => handleInputChange('k0520Parenteral', v)} />
          <CheckboxField label="Feeding tube" checked={formData.k0520FeedingTube} onChange={(v) => handleInputChange('k0520FeedingTube', v)} />
          <CheckboxField label="Mechanically altered diet" checked={formData.k0520MechanicallyAltered} onChange={(v) => handleInputChange('k0520MechanicallyAltered', v)} />
          <CheckboxField label="Therapeutic diet" checked={formData.k0520TherapeuticDiet} onChange={(v) => handleInputChange('k0520TherapeuticDiet', v)} />
          <CheckboxField label="None of the above" checked={formData.k0520None} onChange={(v) => handleInputChange('k0520None', v)} />
        </div>
      </div>

      {/* Hydration */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">Hydration Status</h4>
        <div className="space-y-2">
          {[
            { value: '0', label: '0 - No signs of dehydration' },
            { value: '1', label: '1 - Mild dehydration' },
            { value: '2', label: '2 - Moderate dehydration' },
            { value: '3', label: '3 - Severe dehydration' }
          ].map(opt => (
            <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="hydrationStatus" value={opt.value} checked={formData.hydrationStatus === opt.value} onChange={(e) => handleInputChange('hydrationStatus', e.target.value)} />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Nutrition Notes */}
      <div className="p-4 border rounded-lg">
        <VoiceDictationInput label="Nutrition/Hydration Notes" value={formData.nutritionNotes} onChange={(e) => handleInputChange('nutritionNotes', e.target.value)} rows={3} placeholder="Document nutritional status and interventions..." />
      </div>
    </div>
  );

  // Medications Section
  const renderMedications = () => (
    <div className="space-y-6">
      <div className="text-center border-b pb-4 mb-6">
        <h3 className="text-lg font-bold text-[#f39c12]">Medications & Infusion Therapy</h3>
      </div>

      {/* N0415 High-Risk Drug Classes */}
      <div className="p-4 border rounded-lg bg-red-50">
        <h4 className="font-bold mb-3 text-red-800">N0415. High-Risk Drug Classes in Use</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <CheckboxField label="Anticoagulant (warfarin)" checked={formData.n0415Anticoagulant} onChange={(v) => handleInputChange('n0415Anticoagulant', v)} />
          <CheckboxField label="Antiplatelet" checked={formData.n0415Antiplatelet} onChange={(v) => handleInputChange('n0415Antiplatelet', v)} />
          <CheckboxField label="DOAC (e.g., Eliquis)" checked={formData.n0415DOAC} onChange={(v) => handleInputChange('n0415DOAC', v)} />
          <CheckboxField label="Opioid" checked={formData.n0415Opioid} onChange={(v) => handleInputChange('n0415Opioid', v)} />
          <CheckboxField label="Antipsychotic" checked={formData.n0415Antipsychotic} onChange={(v) => handleInputChange('n0415Antipsychotic', v)} />
          <CheckboxField label="Antibiotic" checked={formData.n0415Antibiotic} onChange={(v) => handleInputChange('n0415Antibiotic', v)} />
          <CheckboxField label="Hypoglycemic" checked={formData.n0415Hypoglycemic} onChange={(v) => handleInputChange('n0415Hypoglycemic', v)} />
          <CheckboxField label="None of the above" checked={formData.n0415None} onChange={(v) => handleInputChange('n0415None', v)} />
        </div>
      </div>

      {/* Medication Management */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">M2001. Drug Regimen Review</h4>
        <p className="text-sm text-gray-600 mb-3">Does a complete drug regimen review indicate potential clinically significant medication issues?</p>
        <div className="space-y-2">
          {[
            { value: '0', label: '0 - No - No issues found during review' },
            { value: '1', label: '1 - Yes - Issues found during review' },
            { value: '9', label: '9 - NA - Patient not taking any medications' }
          ].map(opt => (
            <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="m2001DrugReview" value={opt.value} checked={formData.m2001DrugReview === opt.value} onChange={(e) => handleInputChange('m2001DrugReview', e.target.value)} />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Infusion Therapy */}
      <div className="p-4 border rounded-lg bg-purple-50">
        <h4 className="font-bold mb-3 text-purple-800">O0110. Special Treatments - Infusion Therapy</h4>
        <div className="grid grid-cols-2 gap-3">
          <CheckboxField label="IV Medications" checked={formData.o0110IVMeds} onChange={(v) => handleInputChange('o0110IVMeds', v)} />
          <CheckboxField label="Parenteral Nutrition (TPN)" checked={formData.o0110TPN} onChange={(v) => handleInputChange('o0110TPN', v)} />
          <CheckboxField label="Enteral Nutrition" checked={formData.o0110Enteral} onChange={(v) => handleInputChange('o0110Enteral', v)} />
          <CheckboxField label="None" checked={formData.o0110InfusionNone} onChange={(v) => handleInputChange('o0110InfusionNone', v)} />
        </div>
      </div>

      {/* Medication Notes */}
      <div className="p-4 border rounded-lg">
        <VoiceDictationInput label="Medication Notes" value={formData.medicationNotes} onChange={(e) => handleInputChange('medicationNotes', e.target.value)} rows={4} placeholder="Document medication reconciliation, changes, and education provided..." />
      </div>
    </div>
  );

  // Education Section
  const renderEducation = () => (
    <div className="space-y-6">
      <div className="text-center border-b pb-4 mb-6">
        <h3 className="text-lg font-bold text-[#f39c12]">Patient/Caregiver Education</h3>
      </div>

      {/* Education Topics */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">Education Topics Reviewed</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <CheckboxField label="Disease Process" checked={formData.eduDiseaseProcess} onChange={(v) => handleInputChange('eduDiseaseProcess', v)} />
          <CheckboxField label="Medication Management" checked={formData.eduMedicationManagement} onChange={(v) => handleInputChange('eduMedicationManagement', v)} />
          <CheckboxField label="Diet/Nutrition" checked={formData.eduDietNutrition} onChange={(v) => handleInputChange('eduDietNutrition', v)} />
          <CheckboxField label="Activity/Exercise" checked={formData.eduActivityExercise} onChange={(v) => handleInputChange('eduActivityExercise', v)} />
          <CheckboxField label="Fall Prevention" checked={formData.eduFallPrevention} onChange={(v) => handleInputChange('eduFallPrevention', v)} />
          <CheckboxField label="Wound Care" checked={formData.eduWoundCare} onChange={(v) => handleInputChange('eduWoundCare', v)} />
          <CheckboxField label="Signs/Symptoms to Report" checked={formData.eduSignsSymptoms} onChange={(v) => handleInputChange('eduSignsSymptoms', v)} />
          <CheckboxField label="Emergency Procedures" checked={formData.eduEmergency} onChange={(v) => handleInputChange('eduEmergency', v)} />
          <CheckboxField label="Equipment Use" checked={formData.eduEquipmentUse} onChange={(v) => handleInputChange('eduEquipmentUse', v)} />
          <CheckboxField label="Infection Control" checked={formData.eduInfectionControl} onChange={(v) => handleInputChange('eduInfectionControl', v)} />
          <CheckboxField label="Pain Management" checked={formData.eduPainManagement} onChange={(v) => handleInputChange('eduPainManagement', v)} />
          <CheckboxField label="Skin Care" checked={formData.eduSkinCare} onChange={(v) => handleInputChange('eduSkinCare', v)} />
        </div>
      </div>

      {/* Patient Response */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">Patient/Caregiver Response to Education</h4>
        <div className="space-y-2">
          {[
            { value: 'understood', label: 'Verbalized understanding' },
            { value: 'demonstrated', label: 'Demonstrated understanding' },
            { value: 'needsReinforcement', label: 'Needs reinforcement' },
            { value: 'unableToLearn', label: 'Unable to learn - specify reason' }
          ].map(opt => (
            <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
              <input type="radio" name="patientResponseToEducation" value={opt.value} checked={formData.patientResponseToEducation === opt.value} onChange={(e) => handleInputChange('patientResponseToEducation', e.target.value)} />
              <span className="text-sm">{opt.label}</span>
            </label>
          ))}
        </div>
      </div>

      {/* Teaching Method */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">Teaching Methods Used</h4>
        <div className="flex flex-wrap gap-4">
          <CheckboxField label="Verbal" checked={formData.teachMethodVerbal} onChange={(v) => handleInputChange('teachMethodVerbal', v)} />
          <CheckboxField label="Demonstration" checked={formData.teachMethodDemo} onChange={(v) => handleInputChange('teachMethodDemo', v)} />
          <CheckboxField label="Written Materials" checked={formData.teachMethodWritten} onChange={(v) => handleInputChange('teachMethodWritten', v)} />
          <CheckboxField label="Video/Multimedia" checked={formData.teachMethodVideo} onChange={(v) => handleInputChange('teachMethodVideo', v)} />
        </div>
      </div>

      {/* Education Notes */}
      <div className="p-4 border rounded-lg">
        <VoiceDictationInput label="Education Notes" value={formData.educationNotes} onChange={(e) => handleInputChange('educationNotes', e.target.value)} rows={4} placeholder="Document education provided and patient/caregiver response..." />
      </div>
    </div>
  );

  // Summary Section
  const renderSummary = () => (
    <div className="space-y-6">
      <div className="text-center border-b pb-4 mb-6">
        <h3 className="text-lg font-bold text-[#f39c12]">Recertification Summary</h3>
      </div>

      {/* Clinical Summary */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">Clinical Summary</h4>
        <VoiceDictationInput label="Summary of Patient's Current Status" value={formData.clinicalSummary} onChange={(e) => handleInputChange('clinicalSummary', e.target.value)} rows={4} placeholder="Provide a summary of the patient's current clinical status..." />
      </div>

      {/* Progress Toward Goals */}
      <div className="p-4 border rounded-lg bg-green-50">
        <h4 className="font-bold mb-3 text-green-800">Progress Toward Goals</h4>
        <VoiceDictationInput label="Goal Progress Summary" value={formData.goalProgressSummary} onChange={(e) => handleInputChange('goalProgressSummary', e.target.value)} rows={4} placeholder="Document progress made toward established goals..." />
      </div>

      {/* Continued Need for Services */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">Justification for Continued Services</h4>
        <VoiceDictationInput label="Clinical Justification" value={formData.continuedServicesJustification} onChange={(e) => handleInputChange('continuedServicesJustification', e.target.value)} rows={4} placeholder="Document clinical justification for continued home health services..." />
      </div>

      {/* Planned Interventions */}
      <div className="p-4 border rounded-lg">
        <h4 className="font-bold mb-3">Planned Interventions for Next Certification Period</h4>
        <VoiceDictationInput label="Planned Interventions" value={formData.plannedInterventions} onChange={(e) => handleInputChange('plannedInterventions', e.target.value)} rows={4} placeholder="Document planned interventions and care plan updates..." />
      </div>

      {/* Discipline Visits */}
      <div className="p-4 border rounded-lg bg-blue-50">
        <h4 className="font-bold mb-3 text-blue-800">Recommended Visit Frequency</h4>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <VoiceDictationInput label="SN Visits" value={formData.recertSNVisits} onChange={(e) => handleInputChange('recertSNVisits', e.target.value)} placeholder="e.g., 2W1, 1W4" />
          <VoiceDictationInput label="PT Visits" value={formData.recertPTVisits} onChange={(e) => handleInputChange('recertPTVisits', e.target.value)} placeholder="e.g., 2W1, 1W4" />
          <VoiceDictationInput label="OT Visits" value={formData.recertOTVisits} onChange={(e) => handleInputChange('recertOTVisits', e.target.value)} placeholder="e.g., 2W1, 1W4" />
          <VoiceDictationInput label="SLP Visits" value={formData.recertSLPVisits} onChange={(e) => handleInputChange('recertSLPVisits', e.target.value)} placeholder="e.g., 1W2" />
          <VoiceDictationInput label="MSW Visits" value={formData.recertMSWVisits} onChange={(e) => handleInputChange('recertMSWVisits', e.target.value)} placeholder="e.g., 1W4" />
          <VoiceDictationInput label="HHA Visits" value={formData.recertHHAVisits} onChange={(e) => handleInputChange('recertHHAVisits', e.target.value)} placeholder="e.g., 3W6" />
        </div>
      </div>
    </div>
  );

  const renderCurrentSection = () => {
    switch(currentSection) {
      case 0: return renderSectionA();
      case 1: return renderContacts();
      case 2: return renderAdvanceDirectives();
      case 3: return renderSectionC();  // Neuro/Cognitive
      case 4: return renderSectionD();  // Mental/Psychosocial
      case 5: return renderGoalsSafety();
      case 6: return renderSectionG();
      case 7: return renderSectionGG(); // Section GG - Functional Abilities
      case 8: return renderSectionJ();  // Pain/Cardio
      case 9: return renderVitalsNutrition();
      case 10: return renderSectionM(); // Section M - Skin Conditions
      case 11: return renderMedications();
      case 12: return renderEducation();
      case 13: return renderSummary();
      default: return (
        <div className="text-center py-12">
          <p className="text-gray-600">Section {sections[currentSection].title} - {sections[currentSection].description}</p>
          <p className="text-sm text-gray-500 mt-2">Continue filling out additional sections using voice dictation on all fields.</p>
          <div className="mt-6 p-4 bg-gray-50 rounded-lg">
            <VoiceDictationInput 
              label={`${sections[currentSection].title} Notes`}
              value={formData[`section${currentSection}Notes`] || ''} 
              onChange={(e) => handleInputChange(`section${currentSection}Notes`, e.target.value)} 
              rows={6}
              placeholder="Use voice dictation to add notes for this section..."
            />
          </div>
        </div>
      );
    }
  };

  return (
    <DashboardLayout>
      <div className="p-6 max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <Button variant="ghost" onClick={() => navigate(-1)} className="p-2">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <FileText className="w-8 h-8 text-[#f39c12]" />
              <div>
                <h1 className="text-2xl font-bold text-gray-800">Recertification Assessment</h1>
                <p className="text-sm text-gray-500">OASIS-E with Plan of Care Information</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" onClick={() => exportFillableFromDom(document.getElementById('main-content'), 'Recertification_Assessment.pdf')}>
                <Printer className="w-4 h-4 mr-2" /> Print
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-[#f39c12] hover:bg-[#e67e22]">
                <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save'}
              </Button>
            </div>
          </div>
        </div>

        {visitInfo && (
          <div className="bg-gray-900 text-white rounded-lg p-4 mb-6 flex flex-wrap items-center justify-between gap-3" data-testid="recert-visit-banner">
            <div className="text-sm">
              <span className="text-[#d4af37] font-semibold">Recert visit:</span> {visitInfo.appointment.patientName}, {visitInfo.prefill.visitDate || visitInfo.appointment.date} at {visitInfo.appointment.time}, nurse {visitInfo.assignment.staffName}. Cert period ends {visitInfo.assignment.certPeriodEnd}.
            </div>
            <Button onClick={() => navigate('/dashboard/scheduling')} className="bg-[#d4af37] text-gray-900 hover:bg-[#c9a52f]" data-testid="recert-back-to-schedule">Back to Schedule</Button>
          </div>
        )}

        {/* Section Navigation */}
        <div className="bg-white rounded-lg shadow-sm border p-4 mb-6">
          <div className="flex items-center justify-between mb-4">
            <Button 
              variant="outline" 
              onClick={() => setCurrentSection(Math.max(0, currentSection - 1))}
              disabled={currentSection === 0}
            >
              <ChevronLeft className="w-4 h-4 mr-1" /> Previous
            </Button>
            <span className="font-medium">{sections[currentSection].title}: {sections[currentSection].description}</span>
            <Button 
              variant="outline" 
              onClick={() => setCurrentSection(Math.min(sections.length - 1, currentSection + 1))}
              disabled={currentSection === sections.length - 1}
            >
              Next <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
          <div className="flex gap-1 overflow-x-auto pb-2">
            {sections.map((section, idx) => (
              <button
                key={section.id}
                onClick={() => setCurrentSection(idx)}
                className={`px-3 py-1 text-xs rounded-full whitespace-nowrap ${
                  currentSection === idx 
                    ? 'bg-[#f39c12] text-white' 
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                }`}
              >
                {section.title}
              </button>
            ))}
          </div>
        </div>

        {/* Form Content */}
        <div className="bg-white rounded-lg shadow-sm border p-6">
          {renderCurrentSection()}
        </div>

        {/* Footer Save */}
        <div className="flex justify-end gap-2 mt-6">
          <Button variant="outline" onClick={() => navigate(-1)}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} className="bg-[#f39c12] hover:bg-[#e67e22]">
            <Save className="w-4 h-4 mr-2" /> {saving ? 'Saving...' : 'Save Form'}
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default RecertificationAssessmentForm;
